import { useState, useEffect, useRef, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import type { ProjectFieldKey } from '@/contexts/FieldConfigContext';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { TextareaWithCount } from '@/components/ui/textarea-with-count';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CalendarIcon, ChevronDown, ChevronLeft, ChevronRight, Plus, X, Minus, Sparkles, Star, Info, AlertTriangle, Briefcase, ArrowRight, WandSparkles, Zap } from 'lucide-react';
import { format, differenceInDays, addDays } from 'date-fns';
import { cn } from '@/lib/utils';
import { useFieldConfig } from '@/contexts/FieldConfigContext';
import { Skeleton } from '@/components/ui/skeleton';
import { MultiSelectWithFavorites } from '@/components/ui/multiselect-with-favorites';
import { useIntegrationSettings } from '@/hooks/use-integration-settings';
import { Combobox } from '@/components/ui/combobox';

import { fetchClients, fetchEmployees, getDataMode, checkProjectCodeExists, fetchCustomFieldDefinitions } from '@/lib/data-service';
import { getProjectContentSettings } from '@/lib/project-content-settings-storage';
import type { CustomFieldDefinitionResponse } from '@/lib/data-service';
import { PROJECT_ROLE_DISPLAY_ORDER, PROJECT_ROLE_LABELS } from '@/types';
import type { ProjectRoleType } from '@/types';

import { useProjectStatuses } from '@/contexts/ProjectStatusContext';
import { useTemplates } from '@/contexts/TemplateContext';
import { useInvoiceTemplates } from '@/contexts/InvoiceTemplateContext';
import { useEmailTemplates } from '@/contexts/EmailTemplateContext';
import { PROJECT_STATUS_LABELS } from '@/types';
import type { ProjectCustomFieldDefinition } from '@/types';

const COST_CENTRE_TYPES = ['Department', 'Project', 'Product', 'Service', 'Location', 'Division', 'Team', 'Other'];

// Function to create dynamic form validation schema based on required fields
const createProjectFormSchema = (
  requiredFields: ProjectFieldKey[],
  timelineCompletionRequired: boolean,
  maxLengths?: { projectCode?: number; projectName?: number; description?: number }
) => {
  const isRequired = (field: ProjectFieldKey) => requiredFields.includes(field);
  
  return z.object({
    // Always required fields (isAlwaysMandatory)
    projectName: z.string()
      .min(1, 'Project name is required')
      .max(maxLengths?.projectName || 80, `Project name must not exceed ${maxLengths?.projectName || 80} characters`),
    projectCode: z.string()
      .min(1, 'Project code is required')
      .max(maxLengths?.projectCode || 10, `Project code must not exceed ${maxLengths?.projectCode || 10} characters`),
    // Configurable fields - required based on settings
    description: maxLengths?.description 
      ? z.string().max(maxLengths.description, `Description must not exceed ${maxLengths.description} characters`).optional()
      : z.string().optional(),
    status: isRequired('status') 
      ? z.string().min(1, 'Status is required') 
      : z.string().optional(),
    priority: isRequired('priority') 
      ? z.string().min(1, 'Priority is required') 
      : z.string().optional(),
    type: isRequired('type') 
      ? z.string().min(1, 'Type is required') 
      : z.string().optional(),

    // Timeline fields - handle timeline completion requirement
    startDate: (timelineCompletionRequired || isRequired('startDate'))
      ? z.date({ message: 'Start date is required' }) 
      : z.date().optional(),
    endDate: isRequired('endDate') 
      ? z.date({ message: 'End date is required' }) 
      : z.date().optional(),
    duration: isRequired('duration') 
      ? z.number({ message: 'Duration is required' }).min(1, 'Duration must be at least 1') 
      : z.number().optional(),

    // Key People are handled outside react-hook-form via keyPeopleValues state
    clients: isRequired('clients') 
      ? z.string().min(1, 'Client is required') 
      : z.string().optional(),
    attachments: z.array(z.any()).optional(),
    // Billing configuration fields — type-driven (reads the existing 'type' field, no separate billingType field)
    billable: z.boolean(),
    // T&M-specific
    billingFrequency: z.enum(['weekly', 'fortnightly', 'monthly', 'quarterly', 'on-completion', 'milestone']).optional(),
    // Fixed Fee-specific
    billingTriggerType: z.enum(['date', 'percent-complete']).optional(),
    completionCalcMethod: z.enum(['manual', 'tasks', 'hours', 'milestones']).optional(),
    // Shared
    invoiceTemplateId: z.number().nullable().optional(),
    invoiceEmailTemplateId: z.string().nullable().optional(),
    creditNoteEmailTemplateId: z.string().nullable().optional(),
  }).refine(
    (data) => {
      // If timeline completion is required, validate that user has filled Start Date + (End Date OR Duration)
      if (timelineCompletionRequired) {
        const hasStartDate = !!data.startDate;
        const hasEndDate = !!data.endDate;
        const hasDuration = !!data.duration && data.duration > 0;
        
        // Must have start date AND (end date OR duration)
        return hasStartDate && (hasEndDate || hasDuration);
      }
      return true;
    },
    {
      message: 'Timeline completion requires Start Date and either End Date or Duration',
      path: ['startDate'], // Show error on startDate field
    }
  );
};

// Default schema for type inference (will be overridden with dynamic schema in component)
const projectFormSchema = createProjectFormSchema(['projectName', 'projectCode'], false);

type ProjectFormValues = z.infer<typeof projectFormSchema>;

interface ProjectTemplate {
  id: string;
  templateName: string;
  description?: string;
  templateDescription?: string;
  isFavorite: boolean;
  // Fields that can be pre-filled from template
  priority?: string;
  type?: string;
  defaultStatus?: string;
  defaultDurationDays?: number;
  clients?: string;
  costCentres?: Array<{ name: string; code: string; type?: string }>;
  objectives?: string;
  keyDeliverables?: string;
  usageCount?: number;
  lastUsedAt?: string;
  // 8.17 — Resource budget entries copied from template to new project
  resourceBudget?: Array<{ role: string; totalHours: number; hourlyRate: number }>;
  // 10.D — Default Project Manager employee ID
  defaultProjectManager?: string;
  // 12 — Billing configuration (type-driven by the existing 'type' field)
  billable?: boolean;
  billingFrequency?: 'weekly' | 'fortnightly' | 'monthly' | 'quarterly' | 'on-completion' | 'milestone';
  billingTriggerType?: 'date' | 'percent-complete';
  completionCalcMethod?: 'manual' | 'tasks' | 'hours' | 'milestones';
  invoiceTemplateId?: number | null;
  invoiceEmailTemplateId?: string | null;
  creditNoteEmailTemplateId?: string | null;
}

/** 8.17 — Resource budget entry shape passed from template to project creation */
interface ProjectFormResourceBudgetEntry {
  role: string;
  totalHours: number;
  hourlyRate: number;
}

interface ProjectFormProps {
  onSubmit: (data: ProjectFormValues & { projectCustomFields?: Record<string, string>; costCentres?: Array<{ name: string; code: string; type?: string }>; roleAssignments?: Array<{ role: ProjectRoleType; employeeId: string }>; objectives?: string; keyDeliverables?: string; templateResourceBudget?: ProjectFormResourceBudgetEntry[] }) => void;
  onCancel: () => void;
  onMinimize?: (data: Partial<ProjectFormValues>) => void;
  initialData?: Partial<ProjectFormValues> & { roleAssignments?: Array<{ role: ProjectRoleType; employeeId: string }> };
  isMinimized?: boolean;
  onProgressChange?: (progress: number) => void;
  onIncompleteFieldsChange?: (fields: string[]) => void;
  onFormDataChange?: (data: Partial<ProjectFormValues>) => void;
  quickCreateMode?: boolean;
  onShowFullDetails?: () => void;
  onHideFullDetails?: () => void;
  showFullDetails?: boolean;
  // Template and AI support for Killer UX
  templates?: ProjectTemplate[];
  onTemplateSelect?: (templateId: string) => void;
  selectedTemplate?: string;
  onAiAssist?: () => void;
  highlightedField?: string | null;
  onManageTemplates?: () => void;
  // AI suggested data — pre-fills the form with amber indicators
  aiSuggestedData?: AiSuggestedData | null;
  onAiSuggestedDataConsumed?: () => void;
}

export interface AiSuggestedData {
  projectName?: string;
  description?: string;
  type?: string;
  priority?: string;

  clients?: string;
  startDate?: Date;
  endDate?: Date;
  costCentres?: Array<{ name: string; code: string; type?: string }>;
  objectives?: string;
  keyDeliverables?: string;
}

// Mock data for dropdowns
const priorityOptions = ['Low', 'Medium', 'High', 'Critical'];
const typeOptions = [
  { value: 'fixed-price', label: 'Fixed Price' },
  { value: 'time-and-materials', label: 'Time & Materials' },
];
// Key People role values — keyed by ProjectRoleType, values are employee IDs
type KeyPeopleValues = Partial<Record<ProjectRoleType, string>>;
const clientOptions = [
  'Acme Corp',
  'TechStart Inc',
  'Global Solutions',
  'Innovation Labs',
  'Alpha Industries',
  'Beta Technologies',
  'Gamma Enterprises',
  'Delta Systems',
  'Epsilon Group',
  'Zeta Corporation',
  'Theta Ventures',
  'Iota Partners',
  'Kappa Holdings',
  'Lambda Consulting',
  'Mu Dynamics',
  'Nu Solutions',
  'Xi Innovations',
  'Omicron Tech',
  'Pi Digital',
  'Rho Analytics',
  'Sigma Networks',
  'Tau Services',
  'Upsilon Labs',
  'Phi Enterprises',
  'Chi Systems',
  'Psi Group',
  'Omega Industries',
  'Apex Corporation',
  'Zenith Partners',
  'Vertex Solutions',
  'Summit Technologies',
  'Horizon Ventures',
  'Nexus Group',
  'Quantum Systems',
  'Fusion Enterprises',
  'Catalyst Inc',
  'Momentum Partners',
  'Velocity Corp',
  'Pinnacle Solutions',
  'Vanguard Tech',
];



// Default empty array for templates prop - defined outside component to maintain stable reference
const EMPTY_TEMPLATES: ProjectTemplate[] = [];



// 8.19 — Small amber banner shown in the project creation form when a template
// has resource budget entries, reminding the PM to set monthly distribution.
function ResourceBudgetTemplateBanner({
  entries,
}: {
  entries: ProjectFormResourceBudgetEntry[];
}) {
  const totalHours = entries.reduce((s, e) => s + e.totalHours, 0);
  const totalCost  = entries.reduce((s, e) => s + e.totalHours * e.hourlyRate, 0);
  return (
    <div className="flex items-start gap-2 rounded-md border border-warning-200 bg-warning-50 dark:border-warning-800/50 dark:bg-warning-950/20 px-3 py-2.5">
      <Info className="h-4 w-4 text-warning-600 dark:text-warning-400 shrink-0 mt-0.5" />
      <span className="text-sm text-warning-800 dark:text-warning-300">
        Resource budget pre-filled from template {'\u2014'}{' '}
        <span className="font-semibold">
          {entries.length} role{entries.length !== 1 ? 's' : ''} {'\u00B7'} {totalHours} hrs {'\u00B7'} {'\u00A3'}{totalCost.toLocaleString()}
        </span>. Adjust monthly distribution on the Resource Budget tab after creation.
      </span>
    </div>
  );
}

// Helper component for labels with tooltip, template indicator and AI indicator
const LabelWithTooltip = ({ label, tooltip, align = "center", isFromTemplate = false, isFromAi = false }: { label: string; tooltip?: string; align?: "start" | "center" | "end"; isFromTemplate?: boolean; isFromAi?: boolean }) => {
  const labelContent = (
    <div className="flex items-center gap-1.5">
      <FormLabel>{label}</FormLabel>
      {isFromTemplate && (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium bg-blue-100 text-blue-700 rounded-full">
          From template
        </span>
      )}
      {isFromAi && (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium bg-warning-100 text-warning-700 rounded-full">
          <Sparkles className="h-2.5 w-2.5" />
          AI suggested
        </span>
      )}
      {tooltip && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
          </TooltipTrigger>
          <TooltipContent side="top" align={align} className="max-w-[300px]">
            <p className="text-sm">{tooltip}</p>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
  
  return labelContent;
};

export function ProjectForm({ 
  onSubmit, 
  onCancel,
  onMinimize,
  initialData, 
  isMinimized, 
  onProgressChange, 
  onIncompleteFieldsChange,
  onFormDataChange,
  quickCreateMode = false,
  onShowFullDetails,
  onHideFullDetails,
  showFullDetails = true,
  templates = EMPTY_TEMPLATES,
  onTemplateSelect,
  selectedTemplate,
  onAiAssist,
  highlightedField,
  onManageTemplates,
  aiSuggestedData,
  onAiSuggestedDataConsumed,
}: ProjectFormProps) {
  const { requiredFields, fieldConfigs, getFieldConfig, timelineSettings, generateProjectCode, defaultStatusId, projectCodeSettings, generateExampleCode, roleConfigs: settingsRoleConfigs } = useFieldConfig();
  const { updateTemplate } = useTemplates();
  const { templates: invoiceTemplates } = useInvoiceTemplates();
  const { emailTemplates } = useEmailTemplates();
  // Stores the template's defaultDurationDays so we can apply it when startDate is picked (Task 5.4)
  const pendingDurationDays = useRef<number | null>(null);

  // Derive the customAttributes field state from settings — controls whether Project Attributes section shows
  const customAttributesConfig = fieldConfigs.find(f => f.key === 'customAttributes');
  const customAttributesState = customAttributesConfig?.fieldState ?? 'optional';
  const showCustomAttributes = customAttributesState !== 'hidden';
  const customAttributesRequired = customAttributesState === 'required';

  // Read content field settings — controls whether Objectives / Key Deliverables appear on the form
  const contentFieldSettings = getProjectContentSettings();
  const showObjectives = contentFieldSettings.showObjectives;
  const showKeyDeliverables = contentFieldSettings.showKeyDeliverables;
  const showContentSection = showObjectives || showKeyDeliverables;
  const integrationSettings = useIntegrationSettings();
  const { selectableStatuses } = useProjectStatuses();

  // Build status options from context or fallback to PROJECT_STATUS_LABELS
  const statusOptions = useMemo(() => {
    if (selectableStatuses.length > 0) {
      return selectableStatuses.map(s => ({ value: String(s.id), label: s.name }));
    }
    return Object.entries(PROJECT_STATUS_LABELS).map(([id, label]) => ({ value: id, label }));
  }, [selectableStatuses]);
  
  // Fields to show in Quick Create mode
  // When timeline completion is required, include all timeline fields regardless of individual required status
  // Status and Type are always shown in Quick Create if not Hidden
  const quickCreateFields = useMemo(() => {
    const alwaysShowInQuickCreate: ProjectFieldKey[] = ['status', 'type'];
    const extraFields = alwaysShowInQuickCreate.filter(key => {
      const config = fieldConfigs.find(c => c.key === key);
      return config && config.fieldState !== 'hidden';
    });

    if (timelineSettings.timelineCompletionRequired) {
      // Add timeline fields if not already in required fields
      const timelineFields: ProjectFieldKey[] = ['startDate', 'endDate', 'duration'];
      const fieldsSet = new Set([...requiredFields, ...extraFields, ...timelineFields]);
      return Array.from(fieldsSet);
    }
    const fieldsSet = new Set([...requiredFields, ...extraFields]);
    return Array.from(fieldsSet);
  }, [requiredFields, fieldConfigs, timelineSettings.timelineCompletionRequired]);
  
  // Check if there are any optional fields (fields that are not required)
  const hasOptionalFields = useMemo(() => {
    return fieldConfigs.some(config => !config.isRequired);
  }, [fieldConfigs]);
  
  // Create dynamic schema based on required fields from settings
  const dynamicSchema = useMemo(() => {
    return createProjectFormSchema(
      requiredFields,
      timelineSettings.timelineCompletionRequired,
      {
        projectCode: integrationSettings.projectCodeMaxLength,
        projectName: integrationSettings.projectNameMaxLength,
        description: integrationSettings.descriptionMaxLength,
      }
    );
  }, [requiredFields, timelineSettings.timelineCompletionRequired, integrationSettings]);
  
  const projectNameInputRef = useRef<HTMLInputElement>(null);
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [showCancelWarning, setShowCancelWarning] = useState(false);
  // Initialize hasFormChanges to true if initialData is provided (form was restored from draft)
  const [hasFormChanges, setHasFormChanges] = useState(!!initialData);
  
  // Refs for field elements to enable scrolling
  const fieldRefs = useRef<Record<string, HTMLDivElement | null>>({});
  
  // AI form state
  const [aiProjectDescription, setAiProjectDescription] = useState('');
  const [aiObjectives, setAiObjectives] = useState('');
  const [aiTimeline, setAiTimeline] = useState('');
  
  // Date picker popover state
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);
  // Per-attribute date picker popover open state: keyed by attribute def.id
  const [attrDateOpen, setAttrDateOpen] = useState<Record<string, boolean>>({});
  // Past-date warning state for start date
  const [startDateInPast, setStartDateInPast] = useState(false);
  
  // Controlled month state for End Date calendar to ensure it shows the selected date when reopened
  const [endDateMonth, setEndDateMonth] = useState<Date | undefined>(undefined);
  
  // Always use End Date mode — user picks start + end date, duration is calculated read-only
  const isEndDateMode = true;
  
  // Track which field was last edited for two-way binding between end date and duration

  
  // Track which fields were pre-filled from a template
  const [templatePrefilledFields, setTemplatePrefilledFields] = useState<Set<string>>(new Set());
  const [pendingTemplateSwitch, setPendingTemplateSwitch] = useState<string | null>(null);
  const [templateSwitchDialogOpen, setTemplateSwitchDialogOpen] = useState(false);

  // Track which fields were suggested by AI
  const [aiSuggestedFields, setAiSuggestedFields] = useState<Set<string>>(new Set());

  // Project content fields — Objectives and Key Deliverables
  const [objectives, setObjectives] = useState('');
  const [keyDeliverables, setKeyDeliverables] = useState('');

  // Editable cost centres — populated from template/AI pre-fill, or added manually
  const [costCentres, setCostCentres] = useState<Array<{ name: string; code: string; type?: string }>>([]);
  // Track whether a cost centre row has been "touched" (attempted submit) to show validation errors
  const [costCentreSubmitAttempted, setCostCentreSubmitAttempted] = useState(false);

  // Derive whether cost centres are required from field config settings.
  // Since there is no settings toggle for cost centres, treat them as always
  // required when creating a blank project (no template selected).
  const costCentreFieldConfig = getFieldConfig('costCentres');
  const costCentreIsRequired = costCentreFieldConfig?.isRequired || !selectedTemplate || selectedTemplate === 'none';

  // 8.17 — Resource budget entries copied from template (total hours + rate, no monthly breakdown)
  // Passed through to onSubmit so the project creation handler can pre-populate the Resource Budget tab.
  const [templateResourceBudget, setTemplateResourceBudget] = useState<ProjectFormResourceBudgetEntry[]>([]);
  
  // Project code uniqueness validation state
  const [isCheckingProjectCode, setIsCheckingProjectCode] = useState(false);
  const [projectCodeExists, setProjectCodeExists] = useState(false);
  const projectCodeCheckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Duration is always in days — calculate from start + end dates
  const calculateDurationInDays = (startDate: Date, endDate: Date): number => {
    return differenceInDays(endDate, startDate);
  };
  
  // Fetched clients for Live mode
  const [fetchedClients, setFetchedClients] = useState<Array<{ id: string; name: string; code?: string }>>([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [isErrorClients, setIsErrorClients] = useState(false);
  
  // Fetched employees for Live mode (for Project Manager dropdown)
  const [fetchedEmployees, setFetchedEmployees] = useState<Array<{ id: string; name: string }>>([]);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [isErrorEmployees, setIsErrorEmployees] = useState(false);

  // Key People — role configs come directly from FieldConfigContext (Settings page)
  // settingsRoleConfigs is always up-to-date from localStorage, no API call needed
  const [keyPeopleValues, setKeyPeopleValues] = useState<KeyPeopleValues>(() => {
    // Initialise from initialData.roleAssignments if provided
    const init: KeyPeopleValues = {};
    if (initialData?.roleAssignments) {
      for (const assignment of initialData.roleAssignments as Array<{ role: ProjectRoleType; employeeId: string }>) {
        init[assignment.role] = assignment.employeeId;
      }
    }
    return init;
  });
  // Ref to read latest keyPeopleValues inside template pre-fill effect without causing infinite loops
  const keyPeopleValuesRef = useRef<KeyPeopleValues>({});
  keyPeopleValuesRef.current = keyPeopleValues;
  const [keyPeopleSubmitAttempted, setKeyPeopleSubmitAttempted] = useState(false);

  // Helper: get enabled roles from Settings context
  // A role is shown if it is 'optional' or 'required' (not 'hidden')
  // project-manager is always shown regardless
  const enabledRoles = PROJECT_ROLE_DISPLAY_ORDER.filter((role) => {
    if (role === 'project-manager') return true; // always shown
    const config = settingsRoleConfigs.find((c) => c.role === role);
    return config?.enabled ?? true; // default to showing if no config found
  });

  // Helper: get display label for a role (uses admin-configured label or falls back to default)
  const getRoleLabel = (role: ProjectRoleType): string => {
    const config = settingsRoleConfigs.find((c) => c.role === role);
    return config?.displayLabel || PROJECT_ROLE_LABELS[role];
  };

  // Helper: is a role required?
  const isRoleRequired = (role: ProjectRoleType): boolean => {
    if (role === 'project-manager') return true; // PM always required
    const config = settingsRoleConfigs.find((c) => c.role === role);
    return config?.required ?? false;
  };

  // Helper: get employee options for combobox
  const employeeOptions = fetchedEmployees.map((e) => ({ value: e.id, label: e.name }));

  // Validate key people: returns error message or null
  const getKeyPeopleError = (): string | null => {
    const pmValue = keyPeopleValues['project-manager'];
    if (!pmValue) return 'Project Manager is required';
    for (const role of enabledRoles) {
      if (isRoleRequired(role) && !keyPeopleValues[role]) {
        return `${getRoleLabel(role)} is required`;
      }
    }
    return null;
  };

  // Fetch clients and employees on mount
  useEffect(() => {
    const loadClients = async () => {
      setIsLoadingClients(true);
      try {
        const clients = await fetchClients('project-form');
        // Map to consistent format with id and name
        const mappedClients = clients.map((c: any) => ({
          id: c.id,
          name: c.name,
          code: c.code,
        }));
        setFetchedClients(mappedClients);
      } catch (error) {
        console.error('Failed to fetch clients:', error);
        setIsErrorClients(true);
      } finally {
        setIsLoadingClients(false);
      }
    };
    
    const loadEmployees= async () => {
      setIsLoadingEmployees(true);
      try {
        const employees = await fetchEmployees('project-form');
        // Map to consistent format with id and name
        const mappedEmployees = employees.map((e: any) => ({
          id: e.id,
          name: e.name
        }));
        setFetchedEmployees(mappedEmployees);
      } catch (error) {
        console.error('Failed to fetch employees:', error);
        setIsErrorEmployees(true);
      } finally {
        setIsLoadingEmployees(false);
      }
    };
    
    loadClients();
    loadEmployees();
  }, []);

  // Project Attributes — company-configured custom fields
  const [customFieldDefs, setCustomFieldDefs] = useState<ProjectCustomFieldDefinition[]>([]);
  const [attributeValues, setAttributeValues] = useState<Record<string, string>>({});
  const [isLoadingAttributes, setIsLoadingAttributes] = useState(false);
  const [isErrorAttributes, setIsErrorAttributes] = useState(false);

  useEffect(() => {
    const loadAttributeDefinitions = async () => {
      setIsLoadingAttributes(true);
      try {
        const mode = getDataMode();
        if (mode === 'demo' || mode === 'tour') {
          const { getProjectCustomFieldDefinitions } = await import('@/lib/project-custom-fields-storage');
          const defs = getProjectCustomFieldDefinitions().filter((d) => d.enabled);
          setCustomFieldDefs(defs.sort((a, b) => a.displayOrder - b.displayOrder));
        } else {
          const response = await fetchCustomFieldDefinitions();
          const mapped: ProjectCustomFieldDefinition[] = response
            .filter((d: CustomFieldDefinitionResponse) => d.enabled)
            .map((d: CustomFieldDefinitionResponse) => {
              let parsedOptions: string[] | undefined;
              if (d.options) {
                try {
                  const parsed = JSON.parse(d.options);
                  if (Array.isArray(parsed)) parsedOptions = parsed;
                } catch { /* ignore */ }
              }
              return {
                id: d.id,
                label: d.label,
                fieldType: d.fieldType as ProjectCustomFieldDefinition['fieldType'],
                required: d.required,
                enabled: d.enabled,
                displayOrder: d.displayOrder,
                options: parsedOptions,
                placeholder: d.placeholder ?? undefined,
              };
            });
          setCustomFieldDefs(mapped.sort((a, b) => a.displayOrder - b.displayOrder));
        }
      } catch (error) {
        console.error('Failed to load project attribute definitions:', error);
        setIsErrorAttributes(true);
      } finally {
        setIsLoadingAttributes(false);
      }
    };
    loadAttributeDefinitions();
  }, []);

  const requiredAttributeDefs = customFieldDefs.filter((d) => d.required);
  const hasRequiredAttributes = requiredAttributeDefs.length > 0;

  // In Quick Create mode, sections start collapsed unless showFullDetails is true
  const [sectionsOpen, setSectionsOpen] = useState({
    templates: quickCreateMode ? showFullDetails : true,
    general: quickCreateMode ? showFullDetails : true,
    timeline: quickCreateMode ? showFullDetails : true,
    keyPeople: quickCreateMode ? showFullDetails : true,
    costCentres: quickCreateMode ? showFullDetails : true,
    billing: quickCreateMode ? showFullDetails : true,
    content: quickCreateMode ? showFullDetails : true,
    attributes: quickCreateMode ? showFullDetails : true,
  });

  const form = useForm<ProjectFormValues>({
    resolver: zodResolver(dynamicSchema),
    defaultValues: {
      projectName: initialData?.projectName || '',
      projectCode: initialData?.projectCode || '',
      description: initialData?.description || '',
      status: initialData?.status || (defaultStatusId !== null ? String(defaultStatusId) : ''),
      priority: initialData?.priority || '',
      type: initialData?.type || '',

      startDate: initialData?.startDate,
      endDate: initialData?.endDate,
      duration: initialData?.duration,

      // Key People handled via keyPeopleValues state (not react-hook-form)
      clients: initialData?.clients || '',

      attachments: initialData?.attachments || [],

      billable: true,
      billingFrequency: undefined,
      billingTriggerType: undefined,
      completionCalcMethod: undefined,
      invoiceTemplateId: null,
      invoiceEmailTemplateId: null,
      creditNoteEmailTemplateId: null,
    },
  });

  // Apply default status from Settings when it loads (handles async API load after mount)
  const hasAppliedDefaultStatus = useRef(false);
  useEffect(() => {
    if (hasAppliedDefaultStatus.current) return;
    if (defaultStatusId === null) return;
    const currentStatus = form.getValues('status');
    // Only apply if the status field is still blank and no initialData was provided
    if (!currentStatus && !initialData?.status) {
      form.setValue('status', String(defaultStatusId), { shouldValidate: false });
      hasAppliedDefaultStatus.current = true;
    }
  }, [defaultStatusId, form, initialData?.status]);

  // Calculate form completion percentage
  const calculateProgress = () => {
    // --- 1. Standard required form fields (react-hook-form) ---
    // Exclude fields managed outside RHF — they have dedicated checks below
    // or are not rendered in the project creation form.
    const NON_RHF_FIELDS: Set<string> = new Set([
      'people',           // checked in section 2 (Key People)
      'costCentres',      // checked in section 3 (Cost Centres)
      'customAttributes', // checked in section 4 (Project Attributes)
      'currencyCode',     // not rendered in project creation form
      'budget',           // not rendered in project creation form
      'objectives',       // checked in section 5 (Content fields)
      'keyDeliverables',  // checked in section 5 (Content fields)
    ]);
    const fieldsToCheck = requiredFields.filter(field => !NON_RHF_FIELDS.has(field));
    const values = form.getValues();

    const completedRequiredFields = fieldsToCheck.filter(field => {
      const value = values[field as keyof ProjectFormValues];
      return value !== undefined && value !== null && value !== '';
    });

    const incompleteFormFields = fieldsToCheck
      .filter(field => {
        const value = values[field as keyof ProjectFormValues];
        return value === undefined || value === null || value === '';
      })
      .map(field => {
        const config = getFieldConfig(field);
        return config?.label || field;
      });

    // --- 2. Key People — always required (PM) + any roles marked Required in settings ---
    const requiredRoles = enabledRoles.filter(role => role === 'project-manager' || isRoleRequired(role));
    const completedRequiredRoles = requiredRoles.filter(role => !!keyPeopleValues[role]);
    const incompleteRoleLabels = requiredRoles
      .filter(role => !keyPeopleValues[role])
      .map(role => getRoleLabel(role));

    // --- 3. Cost Centres — always required for blank projects; otherwise per settings ---
    const costCentreComplete = costCentreIsRequired
      ? costCentres.length > 0 && costCentres.every(cc => cc.name.trim() !== '' && cc.code.trim() !== '')
      : true;
    const incompleteCostCentreLabels = costCentreIsRequired && !costCentreComplete
      ? ['Cost Centres']
      : [];

    // --- 4. Project Attributes — any custom field defs marked as required ---
    const incompleteAttributeLabels = customFieldDefs
      .filter(def => def.required)
      .filter(def => {
        const val = attributeValues[def.id];
        return !val || val.trim() === '';
      })
      .map(def => def.label);
    const completedRequiredAttributes = customFieldDefs
      .filter(def => def.required)
      .filter(def => {
        const val = attributeValues[def.id];
        return val && val.trim() !== '';
      });
    const totalRequiredAttributes = customFieldDefs.filter(def => def.required).length;

    // --- 5. Content fields (objectives, keyDeliverables) — managed outside RHF ---
    const contentFieldValues: Record<string, string> = { objectives, keyDeliverables };
    const requiredContentFields = requiredFields.filter(f => f === 'objectives' || f === 'keyDeliverables');
    const completedContentFields = requiredContentFields.filter(f => contentFieldValues[f] && contentFieldValues[f].trim() !== '');
    const incompleteContentLabels = requiredContentFields
      .filter(f => !contentFieldValues[f] || contentFieldValues[f].trim() === '')
      .map(f => getFieldConfig(f)?.label || f);

    // --- Totals ---
    const totalRequired =
      fieldsToCheck.length +
      requiredRoles.length +
      (costCentreIsRequired ? 1 : 0) +
      totalRequiredAttributes +
      requiredContentFields.length;

    const totalCompleted =
      completedRequiredFields.length +
      completedRequiredRoles.length +
      (costCentreIsRequired && costCentreComplete ? 1 : 0) +
      completedRequiredAttributes.length +
      completedContentFields.length;

    const incompleteFields = [
      ...incompleteFormFields,
      ...incompleteRoleLabels,
      ...incompleteCostCentreLabels,
      ...incompleteAttributeLabels,
      ...incompleteContentLabels,
    ];

    const requiredFieldsComplete = incompleteFields.length === 0;

    const progress = totalRequired > 0
      ? Math.round((totalCompleted / totalRequired) * 100)
      : 100;

    return {
      progress,
      incompleteFields,
      canSubmit: requiredFieldsComplete,
    };
  };

  // Generate timeline summary for collapsed accordion header
  const getTimelineSummary = () => {
    const values = form.getValues();
    const startDate = values.startDate;
    const endDate = values.endDate;
    const duration = values.duration;

    // Only show summary if we have both start and end dates
    if (!startDate || !endDate) {
      return null;
    }

    // Calculate duration in days if not explicitly set
    const start = new Date(startDate);
    const end = new Date(endDate);
    const calculatedDuration = duration ?? differenceInDays(end, start);

    // Full format: "15 Jan 2024 - 20 Mar 2024 (65 days)"
    const fullFormat = `${format(start, 'd MMM yyyy')} - ${format(end, 'd MMM yyyy')} (${calculatedDuration} ${calculatedDuration === 1 ? 'day' : 'days'})`;

    // Compact format: "15/01/24 - 20/03/24 (65d)"
    const compactFormat = `${format(start, 'dd/MM/yy')} - ${format(end, 'dd/MM/yy')} (${calculatedDuration}d)`;
    
    return {
      full: fullFormat,
      compact: compactFormat,
    };
  };

  const [progress, setProgress] = useState(calculateProgress().progress);
  const [canSubmit, setCanSubmit] = useState(calculateProgress().canSubmit);
  const [localIncompleteFields, setLocalIncompleteFields] = useState<string[]>(calculateProgress().incompleteFields);
  const [timelineSummary, setTimelineSummary] = useState(getTimelineSummary());

  // Refs to avoid stale closures in form.watch callback — always point to
  // the latest versions so RHF-triggered recalculations see current non-RHF state.
  const calculateProgressRef = useRef(calculateProgress);
  calculateProgressRef.current = calculateProgress;
  const getTimelineSummaryRef = useRef(getTimelineSummary);
  getTimelineSummaryRef.current = getTimelineSummary;

  // Generate template summary for collapsed accordion header
  const getTemplateSummary = () => {
    if (!selectedTemplate || selectedTemplate === 'none') {
      return null;
    }
    
    const template = templates.find(t => t.id === selectedTemplate);
    return template?.templateName || null;
  };

  const [templateSummary, setTemplateSummary] = useState(getTemplateSummary());

  // Watch 'type' (the project type field) to drive conditional billing fields — no separate billingType field
  const watchedBillingType = form.watch('type');

  // Update progress and timeline summary when form values change.
  // Uses refs so the callback always reads current non-RHF state
  // (key people, cost centres, attributes, etc.) even though the
  // subscription is only recreated when the deps below change.
  useEffect(() => {
    let isInitialMount = true;
    
    const subscription = form.watch(() => {
      const { progress: newProgress, incompleteFields, canSubmit: newCanSubmit } = calculateProgressRef.current();
      setProgress(newProgress);
      setCanSubmit(newCanSubmit);
      setLocalIncompleteFields(incompleteFields);
      setTimelineSummary(getTimelineSummaryRef.current());
      onProgressChange?.(newProgress);
      onIncompleteFieldsChange?.(incompleteFields);
      
      // Skip notifying parent on initial mount
      if (isInitialMount) {
        isInitialMount = false;
        return;
      }
      
      // Notify parent of form data changes for draft saving
      // Only notify if there are actual changes (not just initialization)
      if (hasFormChanges) {
        onFormDataChange?.(form.getValues());
      }
    });
    return () => subscription.unsubscribe();
  }, [form, onProgressChange, onIncompleteFieldsChange, onFormDataChange, hasFormChanges]);

  // Update template summary when selectedTemplate changes
  useEffect(() => {
    setTemplateSummary(getTemplateSummary());
  }, [selectedTemplate, templates]);

  // Stable string key for requiredFields so the effect below compares by
  // value, not by array reference (avoids infinite loops when the context
  // or a test mock returns a fresh array on every render).
  const requiredFieldsKey = requiredFields.join(',');

  // Re-run progress when outside-RHF state changes (Key People, Cost Centres, Attributes)
  // Also re-run when async-loaded data arrives (customFieldDefs, requiredFields, enabledRoles)
  // so the denominator stays accurate.
  useEffect(() => {
    const { progress: newProgress, incompleteFields, canSubmit: newCanSubmit } = calculateProgress();
    setProgress(newProgress);
    setCanSubmit(newCanSubmit);
    setLocalIncompleteFields(incompleteFields);
    onProgressChange?.(newProgress);
    onIncompleteFieldsChange?.(incompleteFields);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyPeopleValues, costCentres, attributeValues, objectives, keyDeliverables, customFieldDefs, requiredFieldsKey, costCentreIsRequired]);

  // Initial progress calculation
  useEffect(() => {
    const { progress: initialProgress, incompleteFields, canSubmit: initialCanSubmit } = calculateProgress();
    setProgress(initialProgress);
    setCanSubmit(initialCanSubmit);
    setLocalIncompleteFields(incompleteFields);
    onProgressChange?.(initialProgress);
    onIncompleteFieldsChange?.(incompleteFields);
  }, []);

  // Debug: Log when showCancelWarning changes
  useEffect(() => {
    console.log('showCancelWarning changed to:', showCancelWarning);
  }, [showCancelWarning]);
  
  // Watch for form changes to detect unsaved data
  useEffect(() => {
    let isInitialMount = true;
    
    const subscription = form.watch((value, { name, type }) => {
      // Skip the initial mount trigger
      if (isInitialMount) {
        isInitialMount = false;
        return;
      }
      
      // Check if any field has a non-empty value (excluding fields with default values)
      const hasData = Object.entries(value).some(([key, val]) => {
        // Skip fields that always have default values
        
        if (val === null || val === undefined || val === '') return false;
        if (Array.isArray(val) && val.length === 0) return false;
        return true;
      });
      
      console.log('Form changed - field:', name, 'type:', type, 'hasData:', hasData);
      // If we have initialData (restored form), always consider it as having changes
      // Otherwise, check if there's actual data in the form
      setHasFormChanges(!!initialData || hasData);
    });
    
    return () => subscription.unsubscribe();
  }, [form, initialData]);

  // Auto-focus on project name field when modal opens
  useEffect(() => {
    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      projectNameInputRef.current?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // When project code mode is automatic or auto-override, pre-fill the generated code
  useEffect(() => {
    if (projectCodeSettings.mode === 'automatic' || projectCodeSettings.mode === 'auto-override') {
      const generatedCode = generateExampleCode();
      form.setValue('projectCode', generatedCode, { shouldValidate: false });
    }
  }, [projectCodeSettings.mode, generateExampleCode, form]);


  // Debounced project code uniqueness validation
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'projectCode') {
        const code = value.projectCode;
        
        // Clear any pending timeout
        if (projectCodeCheckTimeoutRef.current) {
          clearTimeout(projectCodeCheckTimeoutRef.current);
        }
        
        // Reset state if code is empty
        if (!code || code.trim() === '') {
          setProjectCodeExists(false);
          setIsCheckingProjectCode(false);
          return;
        }
        
        // Set checking state and debounce the API call
        setIsCheckingProjectCode(true);
        projectCodeCheckTimeoutRef.current = setTimeout(async () => {
          try {
            const exists = await checkProjectCodeExists(code.trim());
            setProjectCodeExists(exists);
          } catch (error) {
            console.error('Failed to check project code:', error);
            setProjectCodeExists(false);
          } finally {
            setIsCheckingProjectCode(false);
          }
        }, 300);
      }
    });
    
    return () => {
      subscription.unsubscribe();
      if (projectCodeCheckTimeoutRef.current) {
        clearTimeout(projectCodeCheckTimeoutRef.current);
      }
    };
  }, [form]);

  // Update sections when showFullDetails changes in Quick Create mode
  useEffect(() => {
    if (quickCreateMode) {
      setSectionsOpen((prev) => ({
        templates: prev.templates, // Preserve templates section state
        general: showFullDetails,
        timeline: showFullDetails,
        keyPeople: showFullDetails,
        costCentres: showFullDetails,
        billing: showFullDetails,
        content: showFullDetails,
        attributes: showFullDetails,
      }));
    }
  }, [showFullDetails, quickCreateMode]);

  // Pre-fill form fields when a template is selected
  useEffect(() => {
    if (!selectedTemplate || selectedTemplate === 'none') {
      // Clear pre-filled fields tracking when no template is selected
      setTemplatePrefilledFields(new Set());
      setCostCentres([]);
      setTemplateResourceBudget([]);
      return;
    }

    const template = templates.find(t => t.id === selectedTemplate);
    if (!template) return;

    const prefilledFields = new Set<string>();

    // Pre-fill description from template
    if (template.description) {
      form.setValue('description', template.description, { shouldValidate: true });
      prefilledFields.add('description');
    }

    // 5.1 — Pre-fill priority: lowercase to match Combobox option values
    if (template.priority) {
      form.setValue('priority', template.priority.toLowerCase(), { shouldValidate: true });
      prefilledFields.add('priority');
    }

    // Pre-fill type from template
    if (template.type) {
      form.setValue('type', template.type, { shouldValidate: true });
      prefilledFields.add('type');
    }

    // 5.2 — Pre-fill status: only if Settings default hasn't been applied yet
    if (template.defaultStatus) {
      const currentStatus = form.getValues('status');
      if (!currentStatus) {
        form.setValue('status', template.defaultStatus, { shouldValidate: true });
        prefilledFields.add('status');
      }
    }

    // 5.3 — Pre-fill endDate from defaultDurationDays if startDate is already set
    if (template.defaultDurationDays && template.defaultDurationDays > 0) {
      const currentStartDate = form.getValues('startDate');
      if (currentStartDate) {
        const computedEndDate = addDays(currentStartDate, template.defaultDurationDays);
        form.setValue('endDate', computedEndDate, { shouldValidate: true });
        form.setValue('duration', template.defaultDurationDays, { shouldValidate: true });
        prefilledFields.add('endDate');
      } else {
        // 5.4 — Store for later: apply when user picks a startDate
        pendingDurationDays.current = template.defaultDurationDays;
      }
    } else {
      pendingDurationDays.current = null;
    }

    // Pre-fill clients from template
    if (template.clients) {
      form.setValue('clients', template.clients, { shouldValidate: true });
      prefilledFields.add('clients');
    }

    // Copy cost centres from template into the editable list
    if (template.costCentres && template.costCentres.length > 0) {
      setCostCentres(template.costCentres.map(cc => ({ name: cc.name, code: cc.code, type: cc.type || '' })));
      prefilledFields.add('costCentres');
    } else {
      setCostCentres([]);
    }

    // 8.17–8.18 — Copy resource budget entries from template
    // Maps TemplateResourceEntry → { role, totalHours, hourlyRate }
    // Monthly distribution is not set here — that's done by the PM on the project after creation.
    if (template.resourceBudget && template.resourceBudget.length > 0) {
      setTemplateResourceBudget(
        template.resourceBudget.map(e => ({
          role: e.role,
          totalHours: e.totalHours,
          hourlyRate: e.hourlyRate,
        }))
      );
      prefilledFields.add('resourceBudget');
    } else {
      setTemplateResourceBudget([]);
    }

    // 5.5 — Pre-fill objectives (only if field is visible)
    if (template.objectives && showObjectives) {
      setObjectives(template.objectives);
      prefilledFields.add('objectives');
    }

    // 5.6 — Pre-fill keyDeliverables (only if field is visible)
    if (template.keyDeliverables && showKeyDeliverables) {
      setKeyDeliverables(template.keyDeliverables);
      prefilledFields.add('keyDeliverables');
    }

    // Pre-fill billing configuration from template
    // billingType is not pre-filled — the billing section reads from the existing 'type' field
    if (template.billable !== undefined) {
      form.setValue('billable', template.billable, { shouldValidate: true });
      prefilledFields.add('billable');
    }
    if (template.billingFrequency) {
      form.setValue('billingFrequency', template.billingFrequency, { shouldValidate: true });
      prefilledFields.add('billingFrequency');
    }
    if (template.billingTriggerType) {
      form.setValue('billingTriggerType', template.billingTriggerType, { shouldValidate: true });
      prefilledFields.add('billingTriggerType');
    }
    if (template.completionCalcMethod) {
      form.setValue('completionCalcMethod', template.completionCalcMethod, { shouldValidate: true });
      prefilledFields.add('completionCalcMethod');
    }
    if (template.invoiceTemplateId != null) {
      form.setValue('invoiceTemplateId', template.invoiceTemplateId, { shouldValidate: true });
      prefilledFields.add('invoiceTemplateId');
    }
    if (template.invoiceEmailTemplateId) {
      form.setValue('invoiceEmailTemplateId', template.invoiceEmailTemplateId, { shouldValidate: true });
      prefilledFields.add('invoiceEmailTemplateId');
    }
    if (template.creditNoteEmailTemplateId) {
      form.setValue('creditNoteEmailTemplateId', template.creditNoteEmailTemplateId, { shouldValidate: true });
      prefilledFields.add('creditNoteEmailTemplateId');
    }

    // Pre-fill Project Manager from template — only if not already set by the user
    if (template.defaultProjectManager) {
      const currentPm = keyPeopleValuesRef.current['project-manager'];
      if (!currentPm) {
        setKeyPeopleValues((prev) => ({ ...prev, 'project-manager': template.defaultProjectManager! }));
        prefilledFields.add('project-manager');
      }
    }

    setTemplatePrefilledFields(prefilledFields);
    
    // Mark form as having changes since we pre-filled fields
    if (prefilledFields.size > 0) {
      setHasFormChanges(true);
    }
  }, [selectedTemplate, templates, form, showObjectives, showKeyDeliverables]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-fill form with AI suggested data
  useEffect(() => {
    if (!aiSuggestedData) {
      setAiSuggestedFields(new Set());
      return;
    }

    const suggestedFields = new Set<string>();

    if (aiSuggestedData.projectName) {
      form.setValue('projectName', aiSuggestedData.projectName, { shouldValidate: true });
      suggestedFields.add('projectName');
    }
    if (aiSuggestedData.description) {
      form.setValue('description', aiSuggestedData.description, { shouldValidate: true });
      suggestedFields.add('description');
    }
    if (aiSuggestedData.type) {
      form.setValue('type', aiSuggestedData.type, { shouldValidate: true });
      suggestedFields.add('type');
    }
    if (aiSuggestedData.priority) {
      form.setValue('priority', aiSuggestedData.priority, { shouldValidate: true });
      suggestedFields.add('priority');
    }

    if (aiSuggestedData.clients) {
      form.setValue('clients', aiSuggestedData.clients, { shouldValidate: true });
      suggestedFields.add('clients');
    }
    if (aiSuggestedData.startDate) {
      form.setValue('startDate', aiSuggestedData.startDate, { shouldValidate: true });
      suggestedFields.add('startDate');
    }
    if (aiSuggestedData.endDate) {
      form.setValue('endDate', aiSuggestedData.endDate, { shouldValidate: true });
      suggestedFields.add('endDate');
    }
    if (aiSuggestedData.costCentres && aiSuggestedData.costCentres.length > 0) {
      setCostCentres(aiSuggestedData.costCentres.map(cc => ({ name: cc.name, code: cc.code, type: cc.type || '' })));
      suggestedFields.add('costCentres');
    }
    if (aiSuggestedData.objectives) {
      setObjectives(aiSuggestedData.objectives);
      suggestedFields.add('objectives');
    }
    if (aiSuggestedData.keyDeliverables) {
      setKeyDeliverables(aiSuggestedData.keyDeliverables);
      suggestedFields.add('keyDeliverables');
    }

    setAiSuggestedFields(suggestedFields);
    setHasFormChanges(true);
    onAiSuggestedDataConsumed?.();
  }, [aiSuggestedData, form]);

  // Check whether all required Project Attributes have been filled in
  const requiredAttributesFilled = requiredAttributeDefs.every((def) => {
    const val = attributeValues[def.id];
    return val !== undefined && val !== null && val.trim() !== '';
  });

  const incompleteRequiredAttributeLabels = requiredAttributeDefs
    .filter((def) => {
      const val = attributeValues[def.id];
      return !val || val.trim() === '';
    })
    .map((def) => def.label);

  // Cost centre validation — enforced for blank projects and when marked Required in settings
  const costCentreValid = !costCentreIsRequired || (costCentres.length > 0 && costCentres.every(cc => cc.name.trim() !== '' && cc.code.trim() !== ''));

  // In Quick Create mode, form can be submitted when minimum required fields are complete
  // In Legacy mode, form requires all required fields (progress === 100)
  // Also require all required Project Attributes to be filled in, and cost centres if required
  const keyPeopleError = getKeyPeopleError();
  const isFormComplete = (quickCreateMode ? canSubmit : progress === 100) && requiredAttributesFilled && costCentreValid && !keyPeopleError;

  const handleSubmit = (data: ProjectFormValues) => {
    // Gate on cost centre and key people validation before proceeding
    setCostCentreSubmitAttempted(true);
    setKeyPeopleSubmitAttempted(true);
    if (!costCentreValid) return;
    if (keyPeopleError) return;

    const nonEmptyAttrs: Record<string, string> = {};
    for (const [k, v] of Object.entries(attributeValues)) {
      if (v.trim() !== '') nonEmptyAttrs[k] = v;
    }
    const hasAttrs = Object.keys(nonEmptyAttrs).length > 0;

    // Build roleAssignments from keyPeopleValues
    const roleAssignments = enabledRoles
      .filter((role) => keyPeopleValues[role])
      .map((role) => ({ role, employeeId: keyPeopleValues[role]! }));

    // 5.8 — Track template usage: increment usageCount and set lastUsedAt
    if (selectedTemplate && selectedTemplate !== 'none') {
      const usedTemplate = templates.find(t => t.id === selectedTemplate);
      if (usedTemplate) {
        updateTemplate(selectedTemplate, {
          usageCount: (usedTemplate.usageCount ?? 0) + 1,
          lastUsedAt: new Date().toISOString(),
        });
      }
    }

    onSubmit({
      ...data,
      ...(hasAttrs ? { projectCustomFields: nonEmptyAttrs } : {}),
      costCentres: costCentres.filter(cc => cc.name.trim() !== '' && cc.code.trim() !== ''),
      roleAssignments,
      ...(objectives.trim() ? { objectives: objectives.trim() } : {}),
      ...(keyDeliverables.trim() ? { keyDeliverables: keyDeliverables.trim() } : {}),
      // 8.17 — Pass resource budget entries copied from template so the project
      // creation handler can pre-populate the Resource Budget tab.
      ...(templateResourceBudget.length > 0 ? { templateResourceBudget } : {}),
    });
  };

  // Helper function to get field label with dynamic asterisk
  const getFieldLabel = (fieldKey: string): string => {
    const config = getFieldConfig(fieldKey as any);
    if (!config) return fieldKey;
    
    // Dynamically change labels when saving as template
    let label = config.label;
    return `${label}${config.isRequired ? ' *' : ''}`;
  };

  const handleCancelClick = () => {
    console.log('Cancel clicked - hasFormChanges:', hasFormChanges);
    
    if (hasFormChanges) {
      // Show warning dialog
      console.log('Showing warning dialog');
      setShowCancelWarning(true);
    } else {
      // No unsaved data, close immediately
      console.log('No unsaved data, closing immediately');
      onCancel();
    }
  };

  const handleDiscardChanges = () => {
    setShowCancelWarning(false);
    onCancel();
  };

  const handleSaveAsDraft = () => {
    setShowCancelWarning(false);
    if (onMinimize) {
      // Get current form values
      const currentFormData = form.getValues();
      onMinimize(currentFormData);
    } else {
      // Fallback to cancel if minimize is not available
      onCancel();
    }
  };

  const toggleSection = (section: keyof typeof sectionsOpen) => {
    setSectionsOpen(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Handle AI generation
  const handleAiGenerate = () => {
    setAiGenerating(true);
    
    // Simulate AI generation (replace with actual API call)
    setTimeout(() => {
      // Pre-fill form fields with AI-generated data with staggered animation
      const fields = [
        { name: 'projectName', value: aiProjectDescription.split(' ').slice(0, 4).join(' ') || 'AI Generated Project', delay: 0 },
        { name: 'projectCode', value: 'AI-' + Date.now().toString().slice(-6), delay: 100 },
        { name: 'description', value: aiProjectDescription, delay: 200 },
        { name: 'status', value: 'not-started', delay: 300 },
        { name: 'priority', value: 'medium', delay: 400 },
        { name: 'type', value: 'internal', delay: 500 },
      ];

      fields.forEach(({ name, value, delay }) => {
        setTimeout(() => {
          form.setValue(name as any, value);
        }, delay);
      });

      // Set dates with delay
      setTimeout(() => {
        const startDate = new Date();
        const endDate = new Date();
        endDate.setMonth(endDate.getMonth() + 6); // Default 6 months
        form.setValue('startDate', startDate);
        form.setValue('endDate', endDate);
      }, 600);
      
      setTimeout(() => {
        setAiGenerating(false);
        setShowAiPanel(false);
        
        // Show full details after AI generation
        if (onShowFullDetails) {
          onShowFullDetails();
        }
      }, 1000);
    }, 1500);
  };

  // Keyboard shortcuts handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Esc to cancel
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
      // Ctrl/Cmd + Enter to submit (if form is complete)
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && isFormComplete) {
        e.preventDefault();
        form.handleSubmit(handleSubmit)();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFormComplete, onCancel, form]);

  // Handle field highlighting and scrolling
  useEffect(() => {
    console.log('highlightedField changed to:', highlightedField);
    console.log('Available field refs:', Object.keys(fieldRefs.current));
    
    if (highlightedField && fieldRefs.current[highlightedField]) {
      const fieldElement = fieldRefs.current[highlightedField];
      console.log('Field element found:', fieldElement);
      
      if (fieldElement) {
        // Scroll to the field with smooth behaviour
        fieldElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center',
          inline: 'nearest'
        });
        console.log('Scrolled to field:', highlightedField);
        
        // Focus the actual input/textarea/select element inside
        const inputElement = fieldElement.querySelector('input, textarea, select') as HTMLElement;
        if (inputElement) {
          // Small delay to ensure scroll completes before focusing
          setTimeout(() => {
            inputElement.focus();
            console.log('Focused input element');
          }, 300);
        }
      }
    } else if (highlightedField) {
      console.log('Field ref not found for:', highlightedField);
    }
  }, [highlightedField]);

  // Helper function to wrap field with ref and highlight styling
  const wrapFieldWithHighlight = (fieldKey: string, fieldContent: React.ReactNode) => {
    return (
      <div 
        key={fieldKey}
        ref={(el) => { fieldRefs.current[fieldKey] = el; }}
        className={cn(
          "transition-all duration-300 rounded-md",
          highlightedField === fieldKey && "ring-2 ring-error-500 ring-offset-2 p-2 -m-2 animate-pulse-scale"
        )}
      >
        {fieldContent}
      </div>
    );
  };

  // Helper function to render a field based on its configuration
  const renderField = (fieldKey: string) => {
    const config = getFieldConfig(fieldKey as any);
    if (!config) return null;

    const isRequired = config.isRequired;
    const label = `${config.label}${isRequired ? ' *' : ''}`;
    const isFromTemplate = templatePrefilledFields.has(fieldKey);
    const isFromAi = aiSuggestedFields.has(fieldKey);

    switch (fieldKey) {
      case 'projectName':
        return wrapFieldWithHighlight(fieldKey,
          <FormField
            control={form.control}
            name="projectName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{label}</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="Enter project name" 
                    className="text-lg font-medium"
                    autoComplete="off"
                    aria-label="Project name"
                    aria-required={isRequired}
                    maxLength={integrationSettings.projectNameMaxLength}
                    {...field}
                    ref={(e) => {
                      field.ref(e);
                      if (quickCreateMode) {
                        projectNameInputRef.current = e;
                      }
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'projectCode': {
        const codeMode = projectCodeSettings.mode;
        const isAutomatic = codeMode === 'automatic';
        const isAutoOverride = codeMode === 'auto-override';

        return wrapFieldWithHighlight(fieldKey,
          <FormField
            control={form.control}
            name="projectCode"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center gap-1.5">
                  <FormLabel>{label}</FormLabel>
                  {/* Only show the wand button in manual mode */}
                  {!isAutomatic && !isAutoOverride && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5 rounded-sm text-muted-foreground hover:text-foreground"
                          onClick={() => {
                            const projectName = form.getValues('projectName');
                            if (projectName) {
                              const generatedCode = generateProjectCode(projectName, integrationSettings.projectCodeMaxLength);
                              form.setValue('projectCode', generatedCode, { shouldValidate: true });
                            }
                          }}
                        >
                          <WandSparkles className="h-3 w-3" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        <p className="text-sm">Auto-generate code from project name</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>

                {/* Automatic mode — read-only display of next code */}
                {isAutomatic ? (
                  <div className="flex items-center gap-2">
                    <div className="flex h-9 w-full items-center rounded-md border border-input bg-muted/50 px-3 py-1">
                      <span className="font-mono text-sm font-medium tracking-wide">{field.value || generateExampleCode()}</span>
                    </div>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex items-center gap-1 rounded-md bg-primary/10 px-2 py-1 text-xs font-medium text-primary">
                          <Zap className="h-3 w-3" />
                          Auto
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        <p className="text-sm">This code is automatically assigned and cannot be changed</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                ) : (
                  /* Manual or Auto+Override — editable input */
                  <FormControl>
                    <div className="relative">
                      {isAutoOverride && (
                        <div className="absolute left-3 top-1/2 -translate-y-1/2">
                          <Zap className="h-3 w-3 text-primary" />
                        </div>
                      )}
                      <Input
                        placeholder={isAutoOverride ? generateExampleCode() : "e.g., PRJ-2024-001"}
                        maxLength={integrationSettings.projectCodeMaxLength}
                        aria-label="Project code"
                        aria-required={isRequired}
                        className={cn(
                          isAutoOverride && "pl-8",
                          projectCodeExists && "border-destructive focus-visible:ring-destructive"
                        )}
                        {...field}
                      />
                      {isCheckingProjectCode && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
                        </div>
                      )}
                    </div>
                  </FormControl>
                )}

                {isAutoOverride && !isAutomatic && (
                  <p className="text-xs text-muted-foreground">
                    Auto-generated — edit to override
                  </p>
                )}
                {projectCodeExists && (
                  <p className="text-sm font-medium text-destructive">
                    A project with this code already exists
                  </p>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        );
      }

      case 'status':
        return wrapFieldWithHighlight(fieldKey,
          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <LabelWithTooltip label={label} isFromTemplate={templatePrefilledFields.has('status')} isFromAi={aiSuggestedFields.has('status')} />
                <FormControl>
                  <Combobox
                    options={statusOptions}
                    value={field.value}
                    onValueChange={field.onChange}
                    placeholder="Select status"
                    searchPlaceholder="Search status..."
                    emptyMessage="No status found."
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'priority':
        return wrapFieldWithHighlight(fieldKey,
          <FormField
            control={form.control}
            name="priority"
            render={({ field }) => (
              <FormItem>
                <LabelWithTooltip label={label} isFromTemplate={isFromTemplate} isFromAi={isFromAi} />
                <FormControl>
                  <Combobox
                    options={priorityOptions.map(option => ({ 
                      value: option.toLowerCase(), 
                      label: option 
                    }))}
                    value={field.value}
                    onValueChange={field.onChange}
                    placeholder="Select priority"
                    searchPlaceholder="Search priority..."
                    emptyMessage="No priority found."
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'type':
        return wrapFieldWithHighlight(fieldKey,
          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem>
                <LabelWithTooltip label={label} isFromTemplate={isFromTemplate} isFromAi={isFromAi} />
                <FormControl>
                  <Combobox
                    options={typeOptions}
                    value={field.value}
                    onValueChange={field.onChange}
                    placeholder="Select type"
                    searchPlaceholder="Search type..."
                    emptyMessage="No type found."
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'startDate':
        return wrapFieldWithHighlight(fieldKey,
          <FormField
            control={form.control}
            name="startDate"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>{label}</FormLabel>
                <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                      >
                        {field.value ? (
                          format(field.value, "PPP")
                        ) : (
                          <span>Pick a date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <div className="flex flex-col">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={(date) => {
                          field.onChange(date);
                          setStartDateOpen(false);
                          if (date) {
                            // Past-date warning
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            setStartDateInPast(date < today);
                            // 5.4 — Apply pending template duration if endDate not yet set
                            if (pendingDurationDays.current && pendingDurationDays.current > 0) {
                              const computedEnd = addDays(date, pendingDurationDays.current);
                              form.setValue('endDate', computedEnd, { shouldValidate: true });
                              form.setValue('duration', pendingDurationDays.current, { shouldValidate: true });
                              setTemplatePrefilledFields(prev => new Set([...prev, 'endDate']));
                              pendingDurationDays.current = null;
                            } else {
                              const endDate = form.getValues('endDate');
                              if (endDate) {
                                form.setValue('duration', calculateDurationInDays(date, endDate));
                              }
                            }
                          } else {
                            setStartDateInPast(false);
                          }
                        }}
                        disabled={(date) => date < new Date("1900-01-01")}
                        captionLayout="dropdown"
                        fromYear={1900}
                        toYear={2100}
                        initialFocus
                      />
                      <div className="border-t border-border p-3">
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={() => {
                            const today = new Date();
                            field.onChange(today);
                            setStartDateOpen(false);
                            setStartDateInPast(false); // Today is never in the past
                            // 5.4 — Apply pending template duration if endDate not yet set
                            if (pendingDurationDays.current && pendingDurationDays.current > 0) {
                              const computedEnd = addDays(today, pendingDurationDays.current);
                              form.setValue('endDate', computedEnd, { shouldValidate: true });
                              form.setValue('duration', pendingDurationDays.current, { shouldValidate: true });
                              setTemplatePrefilledFields(prev => new Set([...prev, 'endDate']));
                              pendingDurationDays.current = null;
                            } else {
                              const endDate = form.getValues('endDate');
                              if (endDate) {
                                form.setValue('duration', calculateDurationInDays(today, endDate));
                              }
                            }
                          }}
                        >
                          Today
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
                <FormMessage />
                {startDateInPast && (
                  <p className="flex items-center gap-1.5 text-xs text-warning-600 dark:text-warning-500 mt-1">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    Start date is in the past
                  </p>
                )}
              </FormItem>
            )}
          />
        );

      case 'endDate':
        const startDate = form.watch('startDate');
        const isEndDateDisabled = !startDate || !isEndDateMode;
        return wrapFieldWithHighlight(fieldKey,
          <FormField
            control={form.control}
            name="endDate"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <LabelWithTooltip label={label} isFromTemplate={templatePrefilledFields.has('endDate')} isFromAi={aiSuggestedFields.has('endDate')} />
                <Popover 
                  open={endDateOpen} 
                  onOpenChange={(open) => {
                    setEndDateOpen(open);
                    if (open) {
                      // When opening, set the calendar month to the selected date, start date, or today
                      setEndDateMonth(field.value || startDate || new Date());
                    }
                  }}
                >
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        disabled={isEndDateDisabled}
                        className={cn(
                          "w-full pl-3 text-left font-normal transition-all",
                          !field.value && "text-muted-foreground",
                          isEndDateDisabled && "opacity-50 cursor-not-allowed bg-muted"
                        )}
                      >
                        {field.value ? (
                          format(field.value, "PPP")
                        ) : (
                          <span>
                            {!startDate ? "Set start date first" : !isEndDateMode ? "Calculated from duration" : "Pick a date"}
                          </span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <div className="flex flex-col">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        month={endDateMonth}
                        onMonthChange={setEndDateMonth}
                        onSelect={(date) => {
                          field.onChange(date);
                          setEndDateOpen(false);
                          // Update duration in days when end date changes
                          if (date && startDate) {
                            form.setValue('duration', calculateDurationInDays(startDate, date));
                          }
                        }}
                        disabled={(date) => {
                          // Disable dates before 1900
                          if (date < new Date("1900-01-01")) return true;
                          // Disable dates on or before start date
                          if (startDate) {
                            const nextDay = new Date(startDate);
                            nextDay.setDate(nextDay.getDate() + 1);
                            nextDay.setHours(0, 0, 0, 0);
                            return date < nextDay;
                          }
                          return false;
                        }}
                        captionLayout="dropdown"
                        fromYear={startDate?.getFullYear() || 1900}
                        toYear={2100}
                        fromMonth={startDate || undefined}
                        initialFocus
                      />
                      <div className="border-t border-border p-3">
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={() => {
                            const today = new Date();
                            // Only allow today if it's after start date
                            if (startDate) {
                              const nextDay = new Date(startDate);
                              nextDay.setDate(nextDay.getDate() + 1);
                              nextDay.setHours(0, 0, 0, 0);
                              if (today >= nextDay) {
                                field.onChange(today);
                                setEndDateOpen(false);
                                // Update duration in days when end date set to today
                                form.setValue('duration', calculateDurationInDays(startDate, today));
                              }
                            }
                          }}
                          disabled={!startDate || new Date() <= startDate}
                        >
                          Today
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'duration':
        const startDateForDuration = form.watch('startDate');
        const endDateForDuration = form.watch('endDate');
        const currentDuration = form.watch('duration');

        // Calculate duration in days from start/end if not already set
        const displayDuration = currentDuration
          ?? (startDateForDuration && endDateForDuration
            ? calculateDurationInDays(new Date(startDateForDuration), new Date(endDateForDuration))
            : null);

        return wrapFieldWithHighlight(fieldKey,
          <FormItem className="flex flex-col">
            <label className="text-sm font-medium leading-none">Duration</label>
            <div
              className={cn(
                "flex h-9 w-full items-center rounded-md border border-input bg-muted px-3 py-1 text-sm",
                !startDateForDuration && "opacity-50"
              )}
            >
              {displayDuration != null && startDateForDuration && endDateForDuration
                ? <span>{displayDuration} {displayDuration === 1 ? 'day' : 'days'}</span>
                : <span className="text-muted-foreground">
                    {!startDateForDuration ? 'Set start date first' : 'Set end date to calculate'}
                  </span>
              }
            </div>
          </FormItem>
        );

      case 'people':
        // Dynamic Key People section — renders one Combobox per enabled role
        return (
          <div key="people" className="space-y-3">
            {isLoadingEmployees ? (
              // Skeleton state while employees are loading
              <>
                {[1, 2, 3].map((i) => (
                  <div key={i} className="space-y-1.5">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-9 w-full" />
                  </div>
                ))}
              </>
            ) : isErrorEmployees ? (
              <Alert variant="destructive" size="compact">
                <AlertDescription>Unable to load team members. Please refresh and try again.</AlertDescription>
              </Alert>
            ) : (
              enabledRoles.map((role) => {
              const roleLabel = getRoleLabel(role);
              const required = isRoleRequired(role);
              const value = keyPeopleValues[role] ?? '';
              const showError = keyPeopleSubmitAttempted && required && !value;
              return (
                <FormItem key={role}>
                  <LabelWithTooltip
                    label={`${roleLabel}${required ? ' *' : ''}`}
                    isFromTemplate={templatePrefilledFields.has(role)}
                    isFromAi={false}
                  />
                  <FormLabel className="sr-only">
                    {roleLabel}{required ? ' *' : ''}
                  </FormLabel>
                  <FormControl>
                    <Combobox
                      options={employeeOptions}
                      value={value}
                      onValueChange={(val) => setKeyPeopleValues((prev) => ({ ...prev, [role]: val }))}
                      placeholder={`Select ${roleLabel}…`}
                      searchPlaceholder={`Search ${roleLabel}…`}
                      emptyMessage="No employee found."
                    />
                  </FormControl>
                  {showError && (
                    <p className="text-xs text-destructive">{roleLabel} is required</p>
                  )}
                </FormItem>
              );
            }))}
          </div>
        );

      case 'clients':
        return wrapFieldWithHighlight(fieldKey,
          <FormItem>
            <LabelWithTooltip label={label} isFromTemplate={isFromTemplate} isFromAi={isFromAi} />
            {isLoadingClients ? (
              <Skeleton className="h-9 w-full" />
            ) : isErrorClients ? (
              <Alert variant="destructive" size="compact">
                <AlertDescription>Unable to load clients. Please refresh and try again.</AlertDescription>
              </Alert>
            ) : (
              <FormField
                control={form.control}
                name="clients"
                render={({ field }) => (
                  <>
                    <FormControl>
                      <Combobox
                        options={
                          fetchedClients.length > 0
                            ? fetchedClients.map(client => ({ 
                                value: client.id, 
                                label: client.code ? `${client.code} — ${client.name}` : client.name,
                                code: client.code,
                                name: client.name,
                              }))
                            : clientOptions.map(client => ({ 
                                value: client.toLowerCase().replace(/\s+/g, '-'), 
                                label: client,
                              }))
                        }
                        placeholder="Select client..."
                        searchPlaceholder="Search clients..."
                        emptyMessage="No clients found."
                        value={field.value || ''}
                        onValueChange={field.onChange}
                        renderOption={(option) => (
                          <span className="truncate">{option.label}</span>
                        )}
                      />
                    </FormControl>
                    <FormDescription>
                      Client for whom the project is being delivered
                    </FormDescription>
                    <FormMessage />
                  </>
                )}
              />
            )}
          </FormItem>
        );

      case 'description':
        return wrapFieldWithHighlight(fieldKey,
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <LabelWithTooltip label={label} isFromTemplate={isFromTemplate} isFromAi={isFromAi} />
                <FormControl>
                  <TextareaWithCount 
                    placeholder="Provide a detailed description of the project..."
                    className="min-h-24"
                    maxLength={integrationSettings.descriptionMaxLength}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col h-full">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col h-full">
          <div className="flex-1 overflow-y-auto space-y-6 pr-2">

          {/* ── "Using template" banner — shown when panel was opened via "Use Template" (4.6) ── */}
          {selectedTemplate && selectedTemplate !== 'none' && (() => {
            const tpl = templates.find(t => t.id === selectedTemplate);
            return tpl ? (
              <div className="flex items-center gap-2 rounded-md border border-warning-200 bg-warning-50 dark:border-warning-800/50 dark:bg-warning-950/20 px-3 py-2">
                <Briefcase className="h-4 w-4 text-warning-600 dark:text-warning-400 shrink-0" />
                <span className="text-sm text-warning-800 dark:text-warning-300">
                  Using template: <span className="font-semibold">{tpl.templateName}</span>
                </span>
              </div>
            ) : null;
          })()}

          {/* 8.19 — Resource budget pre-filled banner */}
          {templateResourceBudget.length > 0 && (
            <ResourceBudgetTemplateBanner entries={templateResourceBudget} />
          )}

          {/* Template Selection - Always shown first in Quick Create mode */}
          {quickCreateMode && templates.length > 0 && onTemplateSelect && (
            <Collapsible open={sectionsOpen.templates} onOpenChange={() => toggleSection('templates')}>
              <div className="rounded-lg border bg-card">
                <CollapsibleTrigger className="flex w-full items-center justify-between p-4 hover:bg-accent/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <h3 className="text-lg font-semibold">Project Templates</h3>
                    {/* Template Summary - Only visible when collapsed and template is selected */}
                    {!sectionsOpen.templates && templateSummary && (
                      <>
                        <span className="text-muted-foreground">|</span>
                        <span className="text-sm text-muted-foreground truncate">
                          Using: {templateSummary}
                        </span>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-4 flex-shrink-0">
                    <ChevronDown className={cn(
                      "h-5 w-5 transition-transform",
                      sectionsOpen.templates && "rotate-180"
                    )} />
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="p-4 pt-0 space-y-4">
                    {/* Template Selector with Manage Templates aligned right */}
                    <div className="flex items-start justify-between gap-4">
                      <div className="space-y-2 flex-1 max-w-[50%]">
                        <LabelWithTooltip 
                          label="Select Template" 
                          tooltip="Choose a template to start with, or create your own"
                        />
                        <Combobox
                          options={[
                            { value: 'none', label: 'None (blank project)' },
                            ...templates.map(template => ({
                              value: template.id,
                              label: template.templateName,
                              description: template.description,
                              isFavorite: template.isFavorite
                            }))
                          ]}
                          value={selectedTemplate || 'none'}
                          onValueChange={(value) => {
                            // If a template is already applied (fields pre-filled), warn before overwriting
                            if (
                              templatePrefilledFields.size > 0 &&
                              value !== 'none' &&
                              value !== selectedTemplate
                            ) {
                              setPendingTemplateSwitch(value);
                              setTemplateSwitchDialogOpen(true);
                            } else {
                              onTemplateSelect(value);
                            }
                          }}
                          placeholder="Select template"
                          searchPlaceholder="Search templates..."
                          emptyMessage="No template found."
                          className={cn(
                            "w-full transition-all",
                            selectedTemplate && selectedTemplate !== 'none' && "border-primary/50 bg-primary/5"
                          )}
                          renderOption={(option) => (
                            <div className="flex items-start gap-2 w-full">
                              {option.isFavorite && (
                                <Star className="h-3 w-3 fill-warning-500 text-warning-500 shrink-0 mt-0.5" />
                              )}
                              <div className="flex flex-col min-w-0 flex-1">
                                <span className="truncate">{option.label}</span>
                                {option.description && (
                                  <span className="text-xs text-muted-foreground line-clamp-2">{option.description}</span>
                                )}
                              </div>
                            </div>
                          )}
                        />
                      </div>

                      <div className="space-y-2 flex-shrink-0">
                        <LabelWithTooltip 
                          label="Template Management" 
                          tooltip="Edit and organise your project templates"
                          align="end"
                        />
                        <div className="flex items-center h-10">
                          {onManageTemplates && (
                            <span
                              onClick={onManageTemplates}
                              className="text-sm font-medium text-primary hover:underline cursor-pointer"
                            >
                              Manage Templates
                            </span>
                          )}
                        </div>
                      </div>
                    </div>


                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          )}

          {/* Quick Create Section - Only shown in Quick Create mode when NOT showing full details AND there are optional fields */}
          {quickCreateMode && !showFullDetails && hasOptionalFields && (
            <div className="space-y-4">
              <div className="rounded-lg border bg-primary/5 border-primary/20 p-4 space-y-4 animate-in fade-in-0 duration-300">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      Quick Create
                      <Badge variant="secondary" className="text-xs font-normal">Fast Track</Badge>
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Complete the required fields below to create your project quickly.
                    </p>
                  </div>
                </div>

                {/* Dynamically render all required fields */}
                <div className="space-y-4">
                  {quickCreateFields
                    .filter(fieldKey => !['startDate', 'endDate', 'duration', 'people'].includes(fieldKey))
                    .map((fieldKey) => (
                      <div key={fieldKey}>
                        {renderField(fieldKey)}
                      </div>
                    ))}
                  
                  {/* Timeline section - show as grouped section when timeline completion is required */}
                  {timelineSettings.timelineCompletionRequired && (
                    <div className="space-y-4 p-4 border rounded-lg bg-muted/20">
                      <h4 className="text-sm font-semibold">Timeline & Scheduling</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {renderField('startDate')}
                        {renderField('endDate')}
                        {renderField('duration')}
                      </div>
                    </div>
                  )}

                  {/* Required Project Attributes in Quick Create */}
                  {hasRequiredAttributes && (
                    <div className="space-y-4 p-4 border rounded-lg bg-muted/20">
                      <h4 className="text-sm font-semibold">Project Attributes</h4>
                      {requiredAttributeDefs.map((def) => {
                        const value = attributeValues[def.id] ?? '';
                        return (
                          <div key={def.id} className="space-y-1.5">
                            <Label className="text-sm font-medium">
                              {def.label} *
                            </Label>
                            {def.fieldType === 'text' && (
                              <Input value={value} onChange={(e) => setAttributeValues((prev) => ({ ...prev, [def.id]: e.target.value }))} placeholder={def.placeholder ?? ''} className="h-9" />
                            )}
                            {def.fieldType === 'number' && (
                              <Input type="number" value={value} onChange={(e) => setAttributeValues((prev) => ({ ...prev, [def.id]: e.target.value }))} placeholder={def.placeholder ?? ''} className="h-9" />
                            )}
                            {def.fieldType === 'currency' && (
                              <div className="relative">
                                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">£</span>
                                <Input type="number" value={value} onChange={(e) => setAttributeValues((prev) => ({ ...prev, [def.id]: e.target.value }))} placeholder={def.placeholder ?? '0.00'} className="h-9 pl-6" />
                              </div>
                            )}
                            {def.fieldType === 'date' && (
                              <Popover open={attrDateOpen[def.id] ?? false} onOpenChange={(open) => setAttrDateOpen((prev) => ({ ...prev, [def.id]: open }))}>
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="outline"
                                    className={cn('w-full pl-3 text-left font-normal h-9', !value && 'text-muted-foreground')}
                                  >
                                    {value ? format(new Date(value), 'PPP') : <span>Pick a date</span>}
                                    <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                  <Calendar
                                    mode="single"
                                    selected={value ? new Date(value) : undefined}
                                    onSelect={(date) => {
                                      setAttributeValues((prev) => ({ ...prev, [def.id]: date ? format(date, 'yyyy-MM-dd') : '' }));
                                      setAttrDateOpen((prev) => ({ ...prev, [def.id]: false }));
                                    }}
                                    captionLayout="dropdown"
                                    fromYear={1900}
                                    toYear={2100}
                                    initialFocus
                                  />
                                </PopoverContent>
                              </Popover>
                            )}
                            {def.fieldType === 'boolean' && (
                              <div className="flex items-center gap-2 pt-0.5">
                                <Switch checked={value === 'true'} onCheckedChange={(checked) => setAttributeValues((prev) => ({ ...prev, [def.id]: checked ? 'true' : 'false' }))} />
                                <span className="text-sm">{value === 'true' ? 'Yes' : 'No'}</span>
                              </div>
                            )}
                            {def.fieldType === 'select' && def.options && (
                              <Select value={value} onValueChange={(v) => setAttributeValues((prev) => ({ ...prev, [def.id]: v }))}>
                                <SelectTrigger className="h-9"><SelectValue placeholder={def.placeholder ?? 'Select...'} /></SelectTrigger>
                                <SelectContent>{def.options.map((opt) => (<SelectItem key={opt} value={opt}>{opt}</SelectItem>))}</SelectContent>
                              </Select>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                  {/* Required Key People in Quick Create (PM is always required) */}
                  {enabledRoles.some(role => isRoleRequired(role)) && (
                    <div className="space-y-4 p-4 border rounded-lg bg-muted/20">
                      <h4 className="text-sm font-semibold">Key People</h4>
                      {isLoadingEmployees ? (
                        <>
                          {[1].map((i) => (
                            <div key={i} className="space-y-1.5">
                              <Skeleton className="h-4 w-32" />
                              <Skeleton className="h-9 w-full" />
                            </div>
                          ))}
                        </>
                      ) : isErrorEmployees ? (
                        <Alert variant="destructive" size="compact">
                          <AlertDescription>Unable to load team members. Please refresh and try again.</AlertDescription>
                        </Alert>
                      ) : (
                        enabledRoles
                          .filter(role => isRoleRequired(role))
                          .map((role) => {
                            const roleLabel = getRoleLabel(role);
                            const value = keyPeopleValues[role] ?? '';
                            const showError = keyPeopleSubmitAttempted && !value;
                            return (
                              <FormItem key={role}>
                                <FormLabel>
                                  {roleLabel} *
                                </FormLabel>
                                <FormControl>
                                  <Combobox
                                    options={employeeOptions}
                                    value={value}
                                    onValueChange={(val) => setKeyPeopleValues((prev) => ({ ...prev, [role]: val }))}
                                    placeholder={`Select ${roleLabel}…`}
                                    searchPlaceholder={`Search ${roleLabel}…`}
                                    emptyMessage="No employee found."
                                  />
                                </FormControl>
                                {showError && (
                                  <p className="text-xs text-destructive">{roleLabel} is required</p>
                                )}
                              </FormItem>
                            );
                          })
                      )}
                    </div>
                  )}

                  {/* Required Content fields in Quick Create */}
                  {showContentSection && requiredFields.some(f => f === 'objectives' || f === 'keyDeliverables') && (
                    <div className="space-y-4 p-4 border rounded-lg bg-muted/20">
                      <h4 className="text-sm font-semibold">Project Content</h4>
                      {showObjectives && requiredFields.includes('objectives' as ProjectFieldKey) && (
                        <div className="space-y-1.5">
                          <Label className="text-sm font-medium">
                            Project Objectives *
                          </Label>
                          <Textarea
                            placeholder="Describe the key objectives this project aims to achieve..."
                            value={objectives}
                            onChange={(e) => {
                              setObjectives(e.target.value);
                              setHasFormChanges(true);
                            }}
                            className="min-h-[80px] text-sm"
                          />
                        </div>
                      )}
                      {showKeyDeliverables && requiredFields.includes('keyDeliverables' as ProjectFieldKey) && (
                        <div className="space-y-1.5">
                          <Label className="text-sm font-medium">
                            Key Deliverables *
                          </Label>
                          <Textarea
                            placeholder="List the key deliverables expected from this project..."
                            value={keyDeliverables}
                            onChange={(e) => {
                              setKeyDeliverables(e.target.value);
                              setHasFormChanges(true);
                            }}
                            className="min-h-[80px] text-sm"
                          />
                        </div>
                      )}
                    </div>
                  )}

                {/* Cost Centres — Inline editable section (tasks 5.1 + 5.2) */}
                <div className="space-y-2">
                  <LabelWithTooltip
                    label={`Cost Centres${costCentreIsRequired ? ' *' : ''}`}
                    tooltip={costCentreIsRequired ? 'At least one cost centre is required. Add a name and a short code for each.' : 'Optionally add cost centres with a name and short code.'}
                    isFromTemplate={templatePrefilledFields.has('costCentres')}
                    isFromAi={aiSuggestedFields.has('costCentres')}
                  />
                  <div className="space-y-2">
                    {costCentres.map((cc, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Input
                          placeholder="Cost centre name"
                          value={cc.name}
                          onChange={e => {
                            const updated = [...costCentres];
                            updated[i] = { ...updated[i], name: e.target.value };
                            setCostCentres(updated);
                          }}
                          maxLength={40}
                          className="flex-1 h-8 text-sm"
                        />
                        <Select
                          value={cc.type || ''}
                          onValueChange={(value) => {
                            const updated = [...costCentres];
                            updated[i] = { ...updated[i], type: value };
                            setCostCentres(updated);
                          }}
                        >
                          <SelectTrigger className="w-36 h-8 text-sm">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            {COST_CENTRE_TYPES.map((type) => (
                              <SelectItem key={type} value={type}>{type}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          placeholder="Code"
                          value={cc.code}
                          onChange={e => {
                            const updated = [...costCentres];
                            updated[i] = { ...updated[i], code: e.target.value };
                            setCostCentres(updated);
                          }}
                          maxLength={10}
                          className="w-24 h-8 text-sm font-mono"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                          onClick={() => setCostCentres(costCentres.filter((_, idx) => idx !== i))}
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full h-8 text-xs border-dashed"
                      onClick={() => setCostCentres([...costCentres, { name: '', code: '', type: '' }])}
                    >
                      <Plus className="h-3.5 w-3.5 mr-1" />
                      Add cost centre
                    </Button>
                  </div>
                  {costCentreSubmitAttempted && !costCentreValid && (
                    <p className="text-xs text-destructive">
                      {costCentres.length === 0
                        ? 'At least one cost centre is required.'
                        : 'Please fill in both name and code for all cost centres.'}
                    </p>
                  )}
                </div>
                
                {/* Progress indicator for Quick Create */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className={cn(
                      "font-medium transition-colors",
                      progress === 100 && requiredAttributesFilled ? "text-success" : (canSubmit && requiredAttributesFilled) ? "text-primary" : "text-muted-foreground"
                    )}>
                      {progress === 100 && requiredAttributesFilled
                        ? "✓ All fields complete!" 
                        : canSubmit && requiredAttributesFilled
                          ? "✓ All required fields complete! Ready to create."
                          : `Complete ${localIncompleteFields.length + incompleteRequiredAttributeLabels.length} required field${(localIncompleteFields.length + incompleteRequiredAttributeLabels.length) !== 1 ? 's' : ''} to continue`}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">{progress}%</span>
                      <Progress value={progress} className="w-24 h-2" />
                    </div>
                  </div>
                  {(localIncompleteFields.length > 0 || incompleteRequiredAttributeLabels.length > 0) && (!canSubmit || !requiredAttributesFilled) && (
                    <p className="text-xs text-muted-foreground">
                      Missing: {[...localIncompleteFields, ...incompleteRequiredAttributeLabels].slice(0, 3).join(', ')}
                      {[...localIncompleteFields, ...incompleteRequiredAttributeLabels].length > 3 && ` +${[...localIncompleteFields, ...incompleteRequiredAttributeLabels].length - 3} more`}
                    </p>
                  )}
                </div>
              </div>

              {/* Progressive Disclosure Card - Only shown when all required fields (including attributes) are complete */}
              {canSubmit && requiredAttributesFilled && (
                <div className="rounded-lg border bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20 p-6 space-y-4 animate-in fade-in-0 slide-in-from-bottom-2 duration-500">
                  <div className="flex items-start gap-3">
                    <div className="shrink-0 mt-0.5">
                      <Briefcase className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1 space-y-3">
                      <div>
                        <h4 className="text-base font-semibold text-foreground mb-1">
                          Want to add more detail?
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          Expand to fill in additional fields including timeline, team members, budget, and more — based on what's been configured for your organisation.
                        </p>
                      </div>

                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 pt-2">
                        <Button
                          type="button"
                          onClick={onShowFullDetails}
                          className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md"
                        >
                          Show All Fields
                          <ArrowRight className="h-4 w-4 ml-2" />
                        </Button>
                        <span className="text-xs text-muted-foreground">
                          or continue with Quick Create
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* General Info Section - Hidden in Quick Create mode unless showFullDetails is true OR there are no optional fields */}
          {(!quickCreateMode || showFullDetails || !hasOptionalFields) && (
          <Collapsible open={sectionsOpen.general} onOpenChange={() => toggleSection('general')}>
            <div className="rounded-lg border bg-card">
              <CollapsibleTrigger className="flex w-full items-center justify-between p-4 hover:bg-accent/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset">
                <h3 className="text-lg font-semibold">General Information</h3>
                <ChevronDown className={cn(
                  "h-5 w-5 transition-transform",
                  sectionsOpen.general && "rotate-180"
                )} />
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="p-4 pt-0 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="projectName"
                      render={({ field }) => (
                        <FormItem>
                          <LabelWithTooltip label={getFieldLabel('projectName')} isFromAi={aiSuggestedFields.has('projectName')} />
                          <FormControl>
                            <Input placeholder="Enter project name" maxLength={integrationSettings.projectNameMaxLength} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="projectCode"
                      render={({ field }) => (
                        <FormItem>
                          <div className="flex items-center gap-1.5">
                            <FormLabel>{getFieldLabel('projectCode')}</FormLabel>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-5 w-5 rounded-sm text-muted-foreground hover:text-foreground"
                                  onClick={() => {
                                    const projectName = form.getValues('projectName');
                                    if (projectName) {
                                      const generatedCode = generateProjectCode(projectName, integrationSettings.projectCodeMaxLength);
                                      form.setValue('projectCode', generatedCode, { shouldValidate: true });
                                    }
                                  }}
                                >
                                  <WandSparkles className="h-3 w-3" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="top">
                                <p className="text-sm">Auto-generate code from project name</p>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                          <FormControl>
                            <Input
                              placeholder="e.g., PRJ-2024-001"
                              maxLength={integrationSettings.projectCodeMaxLength}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="space-y-4">
                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <div className="flex items-center justify-between">
                            <LabelWithTooltip label={getFieldLabel('description')} isFromTemplate={templatePrefilledFields.has('description')} isFromAi={aiSuggestedFields.has('description')} />
                          </div>
                          <FormControl>
                            <TextareaWithCount 
                              placeholder="Provide a detailed description of the project..."
                              className="min-h-24"
                              maxLength={integrationSettings.descriptionMaxLength}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Inline AI Panel */}
                    {showAiPanel && (
                      <div className="rounded-lg border-2 border-primary/30 bg-primary/5 p-4 space-y-4 animate-in slide-in-from-top-2 duration-300">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            <Sparkles className="h-5 w-5 text-primary" />
                            <div>
                              <h4 className="font-semibold text-sm">AI Project Assistant</h4>
                              <p className="text-xs text-muted-foreground">
                                Describe your project and I'll help fill in the details
                              </p>
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowAiPanel(false)}
                            className="h-7 w-7 p-0"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>

                        <div className="space-y-3">
                          <div className="space-y-2">
                            <Label htmlFor="ai-description" className="text-sm font-medium">
                              What are you building? *
                            </Label>
                            <Textarea
                              id="ai-description"
                              placeholder="e.g., A customer portal with authentication, dashboard, and reporting features..."
                              value={aiProjectDescription}
                              onChange={(e) => setAiProjectDescription(e.target.value)}
                              className="min-h-20 resize-none bg-background"
                            />
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="space-y-2">
                              <Label htmlFor="ai-objectives" className="text-sm font-medium">
                                Key Objectives
                              </Label>
                              <Input
                                id="ai-objectives"
                                placeholder="e.g., Improve customer satisfaction..."
                                value={aiObjectives}
                                onChange={(e) => setAiObjectives(e.target.value)}
                                className="bg-background"
                              />
                            </div>

                            <div className="space-y-2">
                              <Label htmlFor="ai-timeline" className="text-sm font-medium">
                                Timeline
                              </Label>
                              <Input
                                id="ai-timeline"
                                placeholder="e.g., 6 months, Q2 2024..."
                                value={aiTimeline}
                                onChange={(e) => setAiTimeline(e.target.value)}
                                className="bg-background"
                              />
                            </div>
                          </div>

                          <Button
                            type="button"
                            onClick={handleAiGenerate}
                            disabled={!aiProjectDescription.trim() || aiGenerating}
                            className="w-full"
                          >
                            {aiGenerating ? (
                              <>
                                <Sparkles className="h-4 w-4 mr-2 animate-spin" />
                                Generating...
                              </>
                            ) : (
                              <>
                                <Sparkles className="h-4 w-4 mr-2" />
                                Generate Project Details
                              </>
                            )}
                          </Button>
                        </div>

                        <div className="rounded-md bg-background/50 p-3 text-xs text-muted-foreground">
                          <p className="font-medium mb-1">AI will help you with:</p>
                          <ul className="space-y-0.5 list-disc list-inside">
                            <li>Project name and code generation</li>
                            <li>Detailed description and objectives</li>
                            <li>Timeline and milestone suggestions</li>
                            <li>Resource allocation recommendations</li>
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField
                      control={form.control}
                      name="status"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{getFieldLabel('status')}</FormLabel>
                          <FormControl>
                            <Combobox
                              options={statusOptions}
                              value={field.value}
                              onValueChange={field.onChange}
                              placeholder="Select status"
                              searchPlaceholder="Search status..."
                              emptyMessage="No status found."
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="priority"
                      render={({ field }) => (
                        <FormItem>
                          <LabelWithTooltip label={getFieldLabel('priority')} isFromTemplate={templatePrefilledFields.has('priority')} isFromAi={aiSuggestedFields.has('priority')} />
                          <FormControl>
                            <Combobox
                              options={priorityOptions.map(option => ({ 
                                value: option.toLowerCase(), 
                                label: option 
                              }))}
                              value={field.value}
                              onValueChange={field.onChange}
                              placeholder="Select priority"
                              searchPlaceholder="Search priority..."
                              emptyMessage="No priority found."
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="type"
                      render={({ field }) => (
                        <FormItem>
                          <LabelWithTooltip label={getFieldLabel('type')} isFromTemplate={templatePrefilledFields.has('type')} isFromAi={aiSuggestedFields.has('type')} />
                          <FormControl>
                            <Combobox
                              options={typeOptions}
                              value={field.value}
                              onValueChange={field.onChange}
                              placeholder="Select type"
                              searchPlaceholder="Search type..."
                              emptyMessage="No type found."
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Client */}
                  {fieldConfigs.find(f => f.key === 'clients')?.fieldState !== 'hidden' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormItem>
                        <LabelWithTooltip
                          label={getFieldLabel('clients')}
                          tooltip="Select the client associated with this project"
                          isFromTemplate={templatePrefilledFields.has('clients')}
                          isFromAi={aiSuggestedFields.has('clients')}
                        />
                        {isLoadingClients ? (
                          <Skeleton className="h-9 w-full" />
                        ) : isErrorClients ? (
                          <Alert variant="destructive" size="compact">
                            <AlertDescription>Unable to load clients. Please refresh and try again.</AlertDescription>
                          </Alert>
                        ) : (
                          <FormField
                            control={form.control}
                            name="clients"
                            render={({ field }) => (
                              <>
                                <FormControl>
                                  <Combobox
                                    options={
                                      fetchedClients.length > 0
                                        ? fetchedClients.map(client => ({
                                            value: client.id,
                                            label: client.code ? `${client.code} — ${client.name}` : client.name,
                                            code: client.code,
                                            name: client.name,
                                          }))
                                        : clientOptions.map(client => ({
                                            value: client.toLowerCase().replace(/\s+/g, '-'),
                                            label: client,
                                          }))
                                    }
                                    placeholder="Select client..."
                                    searchPlaceholder="Search clients..."
                                    emptyMessage="No clients found."
                                    value={field.value || ''}
                                    onValueChange={field.onChange}
                                    renderOption={(option) => (
                                      <span className="truncate">{option.label}</span>
                                    )}
                                  />
                                </FormControl>
                                <FormMessage />
                              </>
                            )}
                          />
                        )}
                      </FormItem>
                    </div>
                  )}

                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
          )}

          {/* Timeline Section - Hidden in Quick Create mode unless showFullDetails is true OR there are no optional fields */}
          {(!quickCreateMode || showFullDetails || !hasOptionalFields) && (
          <Collapsible open={sectionsOpen.timeline} onOpenChange={() => toggleSection('timeline')}>
            <div className="rounded-lg border bg-card">
              <CollapsibleTrigger className="flex w-full items-center justify-between p-4 hover:bg-accent/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <h3 className="text-lg font-semibold">Timeline</h3>
                  {/* Timeline Summary - Only visible when collapsed and dates are filled */}
                  {!sectionsOpen.timeline && timelineSummary && (
                    <>
                      <span className="text-muted-foreground">|</span>
                      {/* Full format on larger screens */}
                      <span className="hidden lg:block text-sm text-muted-foreground truncate">
                        {timelineSummary.full}
                      </span>
                      {/* Compact format on smaller screens */}
                      <span className="lg:hidden text-sm text-muted-foreground truncate">
                        {timelineSummary.compact}
                      </span>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-4 flex-shrink-0">
                  <ChevronDown className={cn(
                    "h-5 w-5 transition-transform",
                    sectionsOpen.timeline && "rotate-180"
                  )} />
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="p-4 pt-0 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {renderField('startDate')}
                    {renderField('endDate')}
                    {renderField('duration')}
                  </div>
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
          )}

          {/* Key People Section */}
          {(!quickCreateMode || showFullDetails || !hasOptionalFields) && (
          <Collapsible open={sectionsOpen.keyPeople} onOpenChange={() => toggleSection('keyPeople')}>
            <div className="rounded-lg border bg-card">
              <CollapsibleTrigger className="flex w-full items-center justify-between p-4 hover:bg-accent/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset">
                <h3 className="text-lg font-semibold">Key People</h3>
                <ChevronDown className={cn(
                  "h-5 w-5 transition-transform",
                  sectionsOpen.keyPeople && "rotate-180"
                )} />
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="p-4 pt-0 space-y-3">
                  {isLoadingEmployees ? (
                    <>
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="space-y-1.5">
                          <Skeleton className="h-4 w-32" />
                          <Skeleton className="h-9 w-full" />
                        </div>
                      ))}
                    </>
                  ) : isErrorEmployees ? (
                    <Alert variant="destructive" size="compact">
                      <AlertDescription>Unable to load team members. Please refresh and try again.</AlertDescription>
                    </Alert>
                  ) : (
                    enabledRoles.map((role) => {
                      const roleLabel = getRoleLabel(role);
                      const required = isRoleRequired(role);
                      const value = keyPeopleValues[role] ?? '';
                      const showError = keyPeopleSubmitAttempted && required && !value;
                      return (
                        <FormItem key={role}>
                          <FormLabel>
                            {roleLabel}{required ? ' *' : ''}
                          </FormLabel>
                          <FormControl>
                            <Combobox
                              options={employeeOptions}
                              value={value}
                              onValueChange={(val) => setKeyPeopleValues((prev) => ({ ...prev, [role]: val }))}
                              placeholder={`Select ${roleLabel}…`}
                              searchPlaceholder={`Search ${roleLabel}…`}
                              emptyMessage="No employee found."
                            />
                          </FormControl>
                          {showError && (
                            <p className="text-xs text-destructive">{roleLabel} is required</p>
                          )}
                        </FormItem>
                      );
                    })
                  )}
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
          )}

          {/* Cost Centres Section */}
          {(!quickCreateMode || showFullDetails || !hasOptionalFields) && (
          <Collapsible open={sectionsOpen.costCentres} onOpenChange={() => toggleSection('costCentres')}>
            <div className="rounded-lg border bg-card">
              <CollapsibleTrigger className="flex w-full items-center justify-between p-4 hover:bg-accent/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset">
                <h3 className="text-lg font-semibold">Cost Centres</h3>
                <ChevronDown className={cn(
                  "h-5 w-5 transition-transform",
                  sectionsOpen.costCentres && "rotate-180"
                )} />
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="p-4 pt-0 space-y-2">
                  <LabelWithTooltip
                    label={`Cost Centres${costCentreIsRequired ? ' *' : ''}`}
                    tooltip={costCentreIsRequired ? 'At least one cost centre is required. Add a name and a short code for each.' : 'Optionally add cost centres with a name and short code.'}
                    isFromTemplate={templatePrefilledFields.has('costCentres')}
                    isFromAi={aiSuggestedFields.has('costCentres')}
                  />
                  <div className="space-y-2">
                    {costCentres.map((cc, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Input
                          placeholder="Cost centre name"
                          value={cc.name}
                          onChange={e => {
                            const updated = [...costCentres];
                            updated[i] = { ...updated[i], name: e.target.value };
                            setCostCentres(updated);
                          }}
                          maxLength={40}
                          className="flex-1 h-9 text-sm"
                        />
                        <Select
                          value={cc.type || ''}
                          onValueChange={(value) => {
                            const updated = [...costCentres];
                            updated[i] = { ...updated[i], type: value };
                            setCostCentres(updated);
                          }}
                        >
                          <SelectTrigger className="w-36 h-9 text-sm">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            {COST_CENTRE_TYPES.map((type) => (
                              <SelectItem key={type} value={type}>{type}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          placeholder="Code"
                          value={cc.code}
                          onChange={e => {
                            const updated = [...costCentres];
                            updated[i] = { ...updated[i], code: e.target.value };
                            setCostCentres(updated);
                          }}
                          maxLength={10}
                          className="w-28 h-9 text-sm font-mono"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive"
                          onClick={() => setCostCentres(costCentres.filter((_, idx) => idx !== i))}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full border-dashed"
                      onClick={() => setCostCentres([...costCentres, { name: '', code: '', type: '' }])}
                    >
                      <Plus className="h-4 w-4 mr-1.5" />
                      Add cost centre
                    </Button>
                  </div>
                  {costCentreSubmitAttempted && !costCentreValid && (
                    <p className="text-xs text-destructive">
                      {costCentres.length === 0
                        ? 'At least one cost centre is required.'
                        : 'Please fill in both name and code for all cost centres.'}
                    </p>
                  )}
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
          )}

          {/* Billing Configuration Section — full form only */}
          {(!quickCreateMode || showFullDetails) && (
          <Collapsible open={sectionsOpen.billing} onOpenChange={() => toggleSection('billing')}>
            <div className="rounded-lg border bg-card">
              <CollapsibleTrigger className="flex w-full items-center justify-between p-4 hover:bg-accent/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset">
                <h3 className="text-lg font-semibold">Billing Configuration</h3>
                <ChevronDown className={cn(
                  "h-5 w-5 transition-transform",
                  sectionsOpen.billing && "rotate-180"
                )} />
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="p-4 pt-0 space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Configure how this project is billed. Billing options are driven by the <strong>Type</strong> selected in the General section above.
                  </p>

                  {/* Billable toggle — always visible */}
                  <FormField
                    control={form.control}
                    name="billable"
                    render={({ field }) => (
                      <FormItem className="flex items-center justify-between rounded-lg border p-3 shadow-sm">
                        <div className="space-y-0.5">
                          <FormLabel className="text-sm font-medium">Billable</FormLabel>
                          <p className="text-xs text-muted-foreground">Mark this project as billable to the client</p>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value ?? true}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  {/* T&M conditional fields — shown when Type = Time & Materials */}
                  {watchedBillingType === 'time-and-materials' && (
                    <div className="space-y-4 pt-1">
                      <FormField
                        control={form.control}
                        name="billingFrequency"
                        render={({ field }) => (
                          <FormItem>
                            <LabelWithTooltip
                              label="Billing Frequency"
                              tooltip="How often invoices are raised for this project"
                              isFromTemplate={templatePrefilledFields.has('billingFrequency')}
                            />
                            <Select onValueChange={field.onChange} value={field.value ?? ''}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select frequency" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="weekly">Weekly</SelectItem>
                                <SelectItem value="fortnightly">Fortnightly</SelectItem>
                                <SelectItem value="monthly">Monthly</SelectItem>
                                <SelectItem value="quarterly">Quarterly</SelectItem>
                                <SelectItem value="on-completion">On Completion</SelectItem>
                                <SelectItem value="milestone">Milestone</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="invoiceEmailTemplateId"
                          render={({ field }) => (
                            <FormItem>
                              <LabelWithTooltip
                                label="Invoice Email Template"
                                tooltip="Email template used when sending invoices to the client"
                                isFromTemplate={templatePrefilledFields.has('invoiceEmailTemplateId')}
                              />
                              <Select
                                onValueChange={(val) => field.onChange(val === '' ? null : val)}
                                value={field.value ?? ''}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select email template" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {emailTemplates
                                    .filter((t) => t.type === 'invoice')
                                    .map((t) => (
                                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="creditNoteEmailTemplateId"
                          render={({ field }) => (
                            <FormItem>
                              <LabelWithTooltip
                                label="Credit Note Template"
                                tooltip="Email template used when sending credit notes to the client"
                                isFromTemplate={templatePrefilledFields.has('creditNoteEmailTemplateId')}
                              />
                              <Select
                                onValueChange={(val) => field.onChange(val === '' ? null : val)}
                                value={field.value ?? ''}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select credit note template" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {emailTemplates
                                    .filter((t) => t.type === 'credit_note')
                                    .map((t) => (
                                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={form.control}
                        name="invoiceTemplateId"
                        render={({ field }) => (
                          <FormItem>
                            <LabelWithTooltip
                              label="Invoice Template"
                              tooltip="Document template used to generate invoices for this project"
                              isFromTemplate={templatePrefilledFields.has('invoiceTemplateId')}
                            />
                            <Select
                              onValueChange={(val) => field.onChange(val === '' ? null : Number(val))}
                              value={field.value != null ? String(field.value) : ''}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select invoice template" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {invoiceTemplates.map((t) => (
                                  <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}

                  {/* Fixed Fee conditional fields */}
                  {watchedBillingType === 'fixed-price' && (
                    <div className="space-y-4 pt-1">
                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="billingTriggerType"
                          render={({ field }) => (
                            <FormItem>
                              <LabelWithTooltip
                                label="Billing Trigger"
                                tooltip="What triggers a billing milestone to become due"
                                isFromTemplate={templatePrefilledFields.has('billingTriggerType')}
                              />
                              <Select onValueChange={field.onChange} value={field.value ?? ''}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select trigger" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="date">Milestone Date</SelectItem>
                                  <SelectItem value="percent-complete">% Complete</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="completionCalcMethod"
                          render={({ field }) => (
                            <FormItem>
                              <LabelWithTooltip
                                label="Completion % Calculation"
                                tooltip="How the project's completion percentage is derived"
                                isFromTemplate={templatePrefilledFields.has('completionCalcMethod')}
                              />
                              <Select onValueChange={field.onChange} value={field.value ?? ''}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select method" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="manual">Manual Entry</SelectItem>
                                  <SelectItem value="tasks">Based on Tasks Completed</SelectItem>
                                  <SelectItem value="hours">Based on Hours Logged</SelectItem>
                                  <SelectItem value="milestones">Based on Milestones</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="invoiceEmailTemplateId"
                          render={({ field }) => (
                            <FormItem>
                              <LabelWithTooltip
                                label="Invoice Email Template"
                                tooltip="Email template used when sending invoices to the client"
                                isFromTemplate={templatePrefilledFields.has('invoiceEmailTemplateId')}
                              />
                              <Select
                                onValueChange={(val) => field.onChange(val === '' ? null : val)}
                                value={field.value ?? ''}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select email template" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {emailTemplates
                                    .filter((t) => t.type === 'invoice')
                                    .map((t) => (
                                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="creditNoteEmailTemplateId"
                          render={({ field }) => (
                            <FormItem>
                              <LabelWithTooltip
                                label="Credit Note Template"
                                tooltip="Email template used when sending credit notes to the client"
                                isFromTemplate={templatePrefilledFields.has('creditNoteEmailTemplateId')}
                              />
                              <Select
                                onValueChange={(val) => field.onChange(val === '' ? null : val)}
                                value={field.value ?? ''}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select credit note template" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {emailTemplates
                                    .filter((t) => t.type === 'credit_note')
                                    .map((t) => (
                                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={form.control}
                        name="invoiceTemplateId"
                        render={({ field }) => (
                          <FormItem>
                            <LabelWithTooltip
                              label="Invoice Template"
                              tooltip="Document template used to generate invoices for this project"
                              isFromTemplate={templatePrefilledFields.has('invoiceTemplateId')}
                            />
                            <Select
                              onValueChange={(val) => field.onChange(val === '' ? null : Number(val))}
                              value={field.value != null ? String(field.value) : ''}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select invoice template" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {invoiceTemplates.map((t) => (
                                  <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}

                  {/* Placeholder when no type selected in General */}
                  {!watchedBillingType && (
                    <p className="text-sm text-muted-foreground italic">
                      Set the project <strong>Type</strong> in the General section to Fixed Price or Time &amp; Materials to configure billing details.
                    </p>
                  )}
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
          )}

          {/* Project Content Section — Objectives and Key Deliverables */}
          {/* Only shown when at least one content field is enabled in Settings → Project Settings → Content Fields */}
          {showContentSection && (!quickCreateMode || showFullDetails) && (
            <Collapsible open={sectionsOpen.content} onOpenChange={() => toggleSection('content')}>
              <div className="rounded-lg border bg-card">
                <CollapsibleTrigger className="flex w-full items-center justify-between p-4 hover:bg-accent/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold">Project Content</h3>
                    <span className="text-xs text-muted-foreground">
                      {[showObjectives, showKeyDeliverables].filter(Boolean).length} field{[showObjectives, showKeyDeliverables].filter(Boolean).length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <ChevronDown className={cn(
                    "h-5 w-5 transition-transform",
                    sectionsOpen.content && "rotate-180"
                  )} />
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="p-4 pt-0 space-y-4">
                    {showObjectives && (
                      <div className="space-y-1.5">
                        <Label htmlFor="objectives" className={cn(
                          "text-sm font-medium flex items-center gap-1.5",
                          templatePrefilledFields.has('objectives') && "text-warning-600",
                          aiSuggestedFields.has('objectives') && "text-warning-600"
                        )}>
                          Project Objectives
                          {templatePrefilledFields.has('objectives') && !aiSuggestedFields.has('objectives') && (
                            <span className="text-warning-500 text-xs font-normal">from template</span>
                          )}
                          {aiSuggestedFields.has('objectives') && (
                            <Sparkles className="h-3 w-3 text-warning-500" />
                          )}
                        </Label>
                        <Textarea
                          id="objectives"
                          placeholder="Describe the key objectives this project aims to achieve..."
                          value={objectives}
                          onChange={(e) => {
                            setObjectives(e.target.value);
                            setHasFormChanges(true);
                            if (templatePrefilledFields.has('objectives')) {
                              setTemplatePrefilledFields(prev => { const n = new Set(prev); n.delete('objectives'); return n; });
                            }
                            if (aiSuggestedFields.has('objectives')) {
                              setAiSuggestedFields(prev => { const n = new Set(prev); n.delete('objectives'); return n; });
                            }
                          }}
                          className={cn(
                            "min-h-24 resize-none",
                            (templatePrefilledFields.has('objectives') || aiSuggestedFields.has('objectives')) && "border-warning-400 bg-warning-50/50 focus-visible:ring-warning-400"
                          )}
                          aria-label="Project Objectives"
                        />
                      </div>
                    )}
                    {showKeyDeliverables && (
                      <div className="space-y-1.5">
                        <Label htmlFor="keyDeliverables" className={cn(
                          "text-sm font-medium flex items-center gap-1.5",
                          templatePrefilledFields.has('keyDeliverables') && "text-warning-600",
                          aiSuggestedFields.has('keyDeliverables') && "text-warning-600"
                        )}>
                          Key Deliverables
                          {templatePrefilledFields.has('keyDeliverables') && !aiSuggestedFields.has('keyDeliverables') && (
                            <span className="text-warning-500 text-xs font-normal">from template</span>
                          )}
                          {aiSuggestedFields.has('keyDeliverables') && (
                            <Sparkles className="h-3 w-3 text-warning-500" />
                          )}
                        </Label>
                        <Textarea
                          id="keyDeliverables"
                          placeholder="List the key deliverables and outputs this project will produce..."
                          value={keyDeliverables}
                          onChange={(e) => {
                            setKeyDeliverables(e.target.value);
                            setHasFormChanges(true);
                            if (templatePrefilledFields.has('keyDeliverables')) {
                              setTemplatePrefilledFields(prev => { const n = new Set(prev); n.delete('keyDeliverables'); return n; });
                            }
                            if (aiSuggestedFields.has('keyDeliverables')) {
                              setAiSuggestedFields(prev => { const n = new Set(prev); n.delete('keyDeliverables'); return n; });
                            }
                          }}
                          className={cn(
                            "min-h-24 resize-none",
                            (templatePrefilledFields.has('keyDeliverables') || aiSuggestedFields.has('keyDeliverables')) && "border-warning-400 bg-warning-50/50 focus-visible:ring-warning-400"
                          )}
                          aria-label="Key Deliverables"
                        />
                      </div>
                    )}
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          )}

          {/* Project Attributes Section — skeleton while loading, error alert on failure, hidden when no defs after load */}
          {/* Also hidden if admin has set customAttributes to 'hidden' in Settings */}
          {showCustomAttributes && isLoadingAttributes && (!quickCreateMode || showFullDetails || !hasOptionalFields) && (
            <div className="rounded-lg border bg-card">
              <div className="flex items-center justify-between p-4">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-5 w-36" />
                  <Skeleton className="h-4 w-12" />
                </div>
                <Skeleton className="h-5 w-5 rounded" />
              </div>
              <div className="px-4 pb-4 space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="space-y-1.5">
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-9 w-full" />
                  </div>
                ))}
              </div>
            </div>
          )}
          {showCustomAttributes && isErrorAttributes && (!quickCreateMode || showFullDetails || !hasOptionalFields) && (
            <Alert variant="destructive" size="compact">
              <AlertDescription>Unable to load project attributes. Please refresh and try again.</AlertDescription>
            </Alert>
          )}
          {/* Project Attributes Section - Only shown when there are enabled attribute definitions AND not hidden by settings */}
          {showCustomAttributes && !isLoadingAttributes && customFieldDefs.length > 0 && (!quickCreateMode || showFullDetails || !hasOptionalFields) && (
          <Collapsible open={sectionsOpen.attributes} onOpenChange={() => toggleSection('attributes')}>
            <div className="rounded-lg border bg-card">
              <CollapsibleTrigger className="flex w-full items-center justify-between p-4 hover:bg-accent/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-semibold">
                    Project Attributes{customAttributesRequired ? ' *' : ''}
                  </h3>
                  <span className="text-xs text-muted-foreground">
                    {customFieldDefs.length} field{customFieldDefs.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <ChevronDown className={cn(
                  "h-5 w-5 transition-transform",
                  sectionsOpen.attributes && "rotate-180"
                )} />
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="p-4 pt-0 space-y-4">
                  {customFieldDefs.map((def) => {
                    const value = attributeValues[def.id] ?? '';
                    return (
                      <div key={def.id} className="space-y-1.5">
                        <Label className="text-sm font-medium">
                          {def.label}{def.required ? ' *' : ''}
                        </Label>

                        {def.fieldType === 'text' && (
                          <Input
                            value={value}
                            onChange={(e) => setAttributeValues((prev) => ({ ...prev, [def.id]: e.target.value }))}
                            placeholder={def.placeholder ?? ''}
                            className="h-9"
                          />
                        )}

                        {def.fieldType === 'number' && (
                          <Input
                            type="number"
                            value={value}
                            onChange={(e) => setAttributeValues((prev) => ({ ...prev, [def.id]: e.target.value }))}
                            placeholder={def.placeholder ?? ''}
                            className="h-9"
                          />
                        )}

                        {def.fieldType === 'currency' && (
                          <div className="relative">
                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">£</span>
                            <Input
                              type="number"
                              value={value}
                              onChange={(e) => setAttributeValues((prev) => ({ ...prev, [def.id]: e.target.value }))}
                              placeholder={def.placeholder ?? '0.00'}
                              className="h-9 pl-6"
                            />
                          </div>
                        )}

                        {def.fieldType === 'date' && (
                          <Popover open={attrDateOpen[def.id] ?? false} onOpenChange={(open) => setAttrDateOpen((prev) => ({ ...prev, [def.id]: open }))}>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className={cn('w-full pl-3 text-left font-normal h-9', !value && 'text-muted-foreground')}
                              >
                                {value ? format(new Date(value), 'PPP') : <span>Pick a date</span>}
                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={value ? new Date(value) : undefined}
                                onSelect={(date) => {
                                  setAttributeValues((prev) => ({ ...prev, [def.id]: date ? format(date, 'yyyy-MM-dd') : '' }));
                                  setAttrDateOpen((prev) => ({ ...prev, [def.id]: false }));
                                }}
                                captionLayout="dropdown"
                                fromYear={1900}
                                toYear={2100}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                        )}

                        {def.fieldType === 'boolean' && (
                          <div className="flex items-center gap-2 pt-0.5">
                            <Switch
                              checked={value === 'true'}
                              onCheckedChange={(checked) =>
                                setAttributeValues((prev) => ({
                                  ...prev,
                                  [def.id]: checked ? 'true' : 'false',
                                }))
                              }
                            />
                            <span className="text-sm">{value === 'true' ? 'Yes' : 'No'}</span>
                          </div>
                        )}

                        {def.fieldType === 'select' && def.options && (
                          <Select
                            value={value}
                            onValueChange={(v) => setAttributeValues((prev) => ({ ...prev, [def.id]: v }))}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder={def.placeholder ?? 'Select...'} />
                            </SelectTrigger>
                            <SelectContent>
                              {def.options.map((opt) => (
                                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
          )}

          </div>

          {/* Form Actions - Fixed at bottom of modal */}
          <div className="flex gap-3 pt-4 border-t bg-background shrink-0">
            {/* Left side - Mode toggle (only show if Quick Create is available) */}
            {quickCreateMode && hasOptionalFields && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  if (showFullDetails) {
                    onHideFullDetails?.();
                  } else {
                    onShowFullDetails?.();
                  }
                }}
                className="gap-2"
              >
                {showFullDetails ? (
                  <>
                    <ChevronLeft className="h-4 w-4" />
                    Back to Quick Create
                  </>
                ) : (
                  <>
                    Show All Fields
                    <ChevronRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            )}
            
            {/* Right side - Action buttons */}
            <div className="flex items-center gap-3 ml-auto">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancelClick}
                aria-label="Cancel project creation (Esc)"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!isFormComplete || projectCodeExists || isCheckingProjectCode}
                className={cn(
                  "relative transition-all",
                  isFormComplete && !projectCodeExists && !isCheckingProjectCode && "shadow-md hover:shadow-lg"
                )}
                aria-label={
                  projectCodeExists 
                    ? "Cannot create project - code already exists" 
                    : isCheckingProjectCode 
                      ? "Checking project code..." 
                      : isFormComplete 
                        ? "Create project (Ctrl+Enter)" 
                        : "Complete required fields to create project"
                }
              >
                {quickCreateMode && canSubmit ? 'Create Project' : 'Save Project'}
                {isFormComplete && !projectCodeExists && !isCheckingProjectCode && (
                  <span className="ml-2 text-xs opacity-60">⌘↵</span>
                )}
              </Button>
            </div>
          </div>
        </form>
      </Form>

      {/* Unsaved Changes Warning Dialog */}
      <AlertDialog open={showCancelWarning} onOpenChange={setShowCancelWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-warning-100 dark:bg-warning-900/20">
                <AlertTriangle className="h-5 w-5 text-warning-600 dark:text-warning-500" />
              </div>
              <div className="flex-1">
                <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
                <AlertDialogDescription>
                  You have unsaved changes in this form. What would you like to do?
                </AlertDialogDescription>
              </div>
            </div>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-col gap-2 sm:gap-2">
            <AlertDialogCancel 
              onClick={() => setShowCancelWarning(false)}
              className="w-full m-0"
            >
              Go Back to Form
            </AlertDialogCancel>
            <div className="flex gap-2 w-full">
              <AlertDialogAction
                onClick={handleSaveAsDraft}
                className="flex-1 bg-primary hover:bg-primary/90"
              >
                Save as Draft
              </AlertDialogAction>
              <AlertDialogAction
                onClick={handleDiscardChanges}
                className="flex-1 bg-destructive hover:bg-destructive/90 text-destructive-foreground"
              >
                Discard Changes
              </AlertDialogAction>
            </div>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Template Switch Confirmation Dialog */}
      <AlertDialog open={templateSwitchDialogOpen} onOpenChange={setTemplateSwitchDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-warning-500" />
              Switch Template?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Switching templates will overwrite the fields that were pre-filled from the current template. Any changes you've made to those fields will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setTemplateSwitchDialogOpen(false);
              setPendingTemplateSwitch(null);
            }}>
              Keep Current Template
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingTemplateSwitch && onTemplateSelect) {
                  onTemplateSelect(pendingTemplateSwitch);
                }
                setTemplateSwitchDialogOpen(false);
                setPendingTemplateSwitch(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Switch Template
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
